import { db, auth } from '../firebase-config';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { GoogleAuthProvider, signInWithPopup, signOut } from 'firebase/auth';
import {
  doc,
  updateDoc,
  collection,
  addDoc,
  serverTimestamp,
  setDoc,
  deleteDoc,
  getDoc,
  increment,
  arrayUnion,
  Timestamp,
  deleteField,
  writeBatch,
} from 'firebase/firestore';
import { WORLD_BANK_TIERS, DEFAULT_MULTIPLIERS } from '../locales/economicData';
import { languageManifest } from '../locales';
import {
  getCalculatedFee as resolveCalculatedFee,
  hydrateCountryRulesForAdmin,
  buildPublishedPricingPayload,
} from './pricingRules';
import { localizationService } from './localizationService';
import { cycleIsFinalised } from '../utils/renewalCycleLogic';
import {
  geminiGenerateText,
  geminiGenerateJson,
  extractJsonFromGeminiText,
} from './geminiService';
import { getAiPipelineMode } from './aiPipelineSettings';
import { isSchemaV1Pipeline, isSchemaV2Pipeline } from '../constants/aiPipelineModes';
import {
  distillEvidenceSchemaV1,
  runRefineryExtractionSchemaV1,
  generateReportDraftSchemaV1,
} from './aiPipelines/schemaV1';
import {
  runRefineryExtractionSchemaV2,
  generateReportDraftSchemaV2,
} from './aiPipelines/schemaV2';
import {
  normalizeExtractionStations,
  normalizeStationAnalysisVerifications,
} from '../utils/normalizeVerification';
import { computeOverallMaturityScore } from '../utils/overallMaturityScore';
import {
  isValidAccreditationLevel,
  normalizeAccreditationLevel,
  accreditationLevelLabel,
  resolveRegisteredAccreditationLevel,
} from '../utils/accreditationLevel';
import {
  getAuditTurn as resolveAuditTurn,
  getRegistryActionBucket as resolveRegistryActionBucket,
} from '../utils/registryTriage';
import {
  PUBLIC_SHOWCASE_DOC_ID,
  SHOWCASE_SCHEMA_VERSION,
  buildAccreditedShowcaseEntry,
  evaluateAccreditedShowcaseEligibility,
  mergeShowcaseSchools,
  splitShowcaseRows,
  toFirestoreAccreditedRow,
  upsertAccreditedRow,
} from './accreditedShowcaseService';

const FACT_CHECK_SECTION_KEYS = ['general', 's01', 's02', 's03', 's04', 's05', 's06', 's07'];

/** `reports/{sid}.meta.qaPhase` — distinguishes first QA vs post-appeal QA while both use `peer_review`. */
export const QA_PHASE_INITIAL = 'initial';
export const QA_PHASE_POST_APPEAL = 'post_appeal';

export const isPostAppealQa = (reportMeta) =>
  String(reportMeta?.qaPhase || '') === QA_PHASE_POST_APPEAL;

const resetPeerReviewSignOff = async (reportRef) => {
  await updateDoc(reportRef, {
    'certification.peerReviewSigned': false,
    'certification.peerReviewerUid': null,
    'certification.peerReviewerName': null,
    'meta.lastEditedAt': serverTimestamp(),
  });
};

const PRICING_LEVEL_KEYS = ['foundation', 'certified', 'accredited'];
const MAX_PRICING_CHANGES_IN_LOG = 100;

/** Deep-clone maps of numbers/bools for Firestore (drops undefined, avoids spreading Timestamp/FieldValue from reads). */
function cloneJsonSafeMaps(obj) {
  try {
    return JSON.parse(JSON.stringify(obj == null ? {} : obj));
  } catch {
    return {};
  }
}

function sanitizeSupportIssueText(value, maxLen = 2000) {
  return String(value || '').trim().slice(0, maxLen);
}

function sanitizeSupportIssueEnum(value, allowed, fallback) {
  const next = String(value || '').trim().toLowerCase();
  return allowed.includes(next) ? next : fallback;
}

function sanitizeSupportIssueContext(input) {
  try {
    const raw = JSON.parse(JSON.stringify(input == null ? {} : input));
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
    const clean = {};
    for (const [key, value] of Object.entries(raw)) {
      if (value === undefined) continue;
      if (value == null || ['string', 'number', 'boolean'].includes(typeof value)) {
        clean[key] = typeof value === 'string' ? value.slice(0, 800) : value;
        continue;
      }
      if (Array.isArray(value)) {
        clean[key] = value
          .slice(0, 20)
          .map((item) => (typeof item === 'string' ? item.slice(0, 300) : item))
          .filter((item) => item != null);
        continue;
      }
      if (typeof value === 'object') {
        const nested = {};
        for (const [nestedKey, nestedValue] of Object.entries(value).slice(0, 20)) {
          if (nestedValue == null || ['string', 'number', 'boolean'].includes(typeof nestedValue)) {
            nested[nestedKey] = typeof nestedValue === 'string' ? nestedValue.slice(0, 300) : nestedValue;
          }
        }
        clean[key] = nested;
      }
    }
    return clean;
  } catch {
    return {};
  }
}

function buildSanitizedPricingDocForFirestore(config, adminName) {
  return {
    ...buildPublishedPricingPayload(config),
    lastUpdated: serverTimestamp(),
    updatedBy: String((adminName || '').trim() || 'IFIP Staff'),
  };
}

function stripPricingMeta(doc) {
  if (!doc || typeof doc !== 'object') return {};
  const { lastUpdated, updatedBy, ...rest } = doc;
  return rest;
}

function levelDisplayName(level) {
  const k = String(level || '').toLowerCase();
  if (k === 'accredited') return 'Global (Accredited)';
  if (k === 'certified') return 'Certified';
  if (k === 'foundation') return 'Foundation';
  return k || level;
}

/** Build structured + human-readable pricing diff for a single PRICING_UPDATE audit row. */
function buildPricingAuditDetails(previousDoc, incomingConfig) {
  const prev = stripPricingMeta(previousDoc || {});
  const next = stripPricingMeta(incomingConfig || {});
  const changes = [];

  for (const lvl of PRICING_LEVEL_KEYS) {
    const a = prev.baseRates?.[lvl];
    const b = next.baseRates?.[lvl];
    if (a === undefined && b === undefined) continue;
    if (Number(a) !== Number(b)) {
      changes.push({ kind: 'base_rate', level: lvl, before: a, after: b });
    }
  }

  const prevPaused = !!(prev.registrationsPaused || prev.globalMarketsDisabled);
  const nextPaused = !!(next.registrationsPaused || next.globalMarketsDisabled);
  if (prevPaused !== nextPaused) {
    changes.push({
      kind: 'global_availability',
      before: prevPaused,
      after: nextPaused
    });
  }

  const countryKeys = new Set([
    ...Object.keys(prev.countryOverrides || {}),
    ...Object.keys(next.countryOverrides || {})
  ]);
  for (const country of countryKeys) {
    for (const lvl of PRICING_LEVEL_KEYS) {
      const a = prev.countryOverrides?.[country]?.[lvl];
      const b = next.countryOverrides?.[country]?.[lvl];
      if (a === undefined && b === undefined) continue;
      if (Number(a) !== Number(b)) {
        changes.push({ kind: 'country_override', country, level: lvl, before: a, after: b });
      }
    }
  }

  const tierKeys = new Set([
    ...Object.keys(prev.tierOverrides || {}),
    ...Object.keys(next.tierOverrides || {})
  ]);
  for (const tier of tierKeys) {
    for (const lvl of PRICING_LEVEL_KEYS) {
      const a = prev.tierOverrides?.[tier]?.[lvl];
      const b = next.tierOverrides?.[tier]?.[lvl];
      if (a === undefined && b === undefined) continue;
      if (Number(a) !== Number(b)) {
        changes.push({ kind: 'tier_override', tier: String(tier), level: lvl, before: a, after: b });
      }
    }
  }

  const ruleCountries = new Set([
    ...Object.keys(prev.countryRules || {}),
    ...Object.keys(next.countryRules || {}),
  ]);
  for (const country of ruleCountries) {
    for (const lvl of PRICING_LEVEL_KEYS) {
      const a = prev.countryRules?.[country]?.[lvl]?.mode || null;
      const b = next.countryRules?.[country]?.[lvl]?.mode || null;
      if (a !== b) {
        changes.push({
          kind: 'registration_mode',
          country,
          level: lvl,
          before: a || '(default)',
          after: b || '(default)',
        });
      }
    }
  }

  const maxSummaryParts = 35;
  const parts = [];
  for (let i = 0; i < Math.min(changes.length, maxSummaryParts); i++) {
    const ch = changes[i];
    if (ch.kind === 'base_rate') {
      parts.push(`${levelDisplayName(ch.level)} base GBP: ${ch.before ?? '—'} → ${ch.after}`);
    } else if (ch.kind === 'global_availability') {
      parts.push(`All countries automated availability: ${ch.before ? 'OFF' : 'ON'} → ${ch.after ? 'OFF' : 'ON'}`);
    } else if (ch.kind === 'country_override') {
      parts.push(`${ch.country} / ${levelDisplayName(ch.level)}: ${ch.before ?? '—'} → ${ch.after} GBP`);
    } else if (ch.kind === 'tier_override') {
      parts.push(`Tier ${ch.tier} / ${levelDisplayName(ch.level)}: ${ch.before ?? '—'} → ${ch.after} GBP`);
    } else if (ch.kind === 'registration_mode') {
      parts.push(`${ch.country} / ${levelDisplayName(ch.level)}: ${ch.before} → ${ch.after}`);
    } else if (ch.kind === 'market_availability') {
      parts.push(`${ch.country} / ${levelDisplayName(ch.level)}: ${ch.before} → ${ch.after}`);
    }
  }
  if (changes.length > maxSummaryParts) {
    parts.push(`…and ${changes.length - maxSummaryParts} more change(s)`);
  }

  const summary =
    parts.length > 0
      ? parts.join('; ')
      : 'Pricing document saved (no fee matrix changes detected vs previous snapshot).';

  return { changes, changeCount: changes.length, summary };
}

function resolveLangKey(raw) {
  const key = String(raw || '').trim().toLowerCase();
  const aliases = {
    filipino: 'tl',
    tagalog: 'tl',
    pilipino: 'tl',
    fil: 'tl',
    ph: 'tl'
  };
  if (aliases[key]) return aliases[key];
  if (languageManifest[key]) return key;
  const match = Object.entries(languageManifest).find(([, meta]) =>
    [meta.label, meta.nativeName].some((v) => String(v || '').trim().toLowerCase() === key)
  );
  if (match?.[0]) return match[0];
  // Allow runtime-managed codes not yet present in the static manifest.
  if (/^[a-z]{2,5}(-[a-z]{2,5})?$/.test(key)) return key;
  return 'en';
}


function formatAccreditationLevelLabel(level) {
  const key = String(level || 'foundation').toLowerCase();
  if (key === 'certified') return 'Certified';
  if (key === 'accredited' || key === 'global') return 'Global';
  return 'Foundation';
}

function schoolCountryLabel(schoolData = {}) {
  return schoolData.answers?.country || schoolData.country || 'Not set';
}

function schoolEmailContext(schoolData = {}, schoolId = '') {
  const levelRaw = schoolData.appliedOffer?.appliedLevel || schoolData.accreditationLevel || 'foundation';
  return {
    schoolName: schoolData.officialName || schoolId || 'School',
    level: formatAccreditationLevelLabel(levelRaw),
    country: schoolCountryLabel(schoolData),
    orgLead: schoolData.answers?.orgLead || schoolData.answers?.q1 || 'Lead Contact',
    schoolEmail: adminServices.getSchoolEmail(schoolData) || '',
    accreditorName: schoolData.assignedAuditorName || 'Not assigned',
  };
}

function getSchoolLangKey(schoolData) {
  return resolveLangKey(
    schoolData?.currentLanguage ||
    schoolData?.signupLanguage ||
    schoolData?.preferredLang ||
    schoolData?.language ||
    schoolData?.answers?.preferredLang ||
    schoolData?.answers?.language ||
    'en'
  );
}

function generatePublicBadgeId() {
  if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
    const bytes = new Uint8Array(12);
    window.crypto.getRandomValues(bytes);
    return `pb_${Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('')}`;
  }
  return `pb_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

function actorUid() {
  try {
    return auth.currentUser?.uid || null;
  } catch {
    return null;
  }
}

async function broadcastAdminResolutionNotice({
  label,
  category = 'system',
  priority = 'amber',
  excludeUid = null,
  predicate = () => true,
}) {
  try {
    const staff = await adminServices.getApprovedStaff();
    const targets = staff.filter((s) => s.id && s.id !== excludeUid && predicate(s));
    if (!targets.length) return;

    await Promise.all(
      targets.map((s) =>
        adminServices.createNotification(s.id, `RESOLUTION_${Date.now()}_${Math.random().toString(36).slice(2)}`, {
          label,
          priority,
          category,
          type: 'RESOLUTION',
          isAction: false,
          suppressEmail: true,
        })
      )
    );
  } catch (e) {
    console.warn('broadcastAdminResolutionNotice skipped:', e);
  }
}

function safeJsonFromModelText(text) {
  const start = String(text || '').indexOf('{');
  const end = String(text || '').lastIndexOf('}');
  if (start === -1 || end === -1 || end <= start) {
    throw new Error('AI did not return valid JSON.');
  }
  return JSON.parse(String(text).substring(start, end + 1));
}

async function backTranslateFactCheckFeedback(feedback) {
  const payload = FACT_CHECK_SECTION_KEYS.reduce((acc, key) => {
    const raw = feedback?.[key];
    acc[key] = typeof raw === 'string' ? raw : '';
    return acc;
  }, {});
  const hasContent = Object.values(payload).some((v) => String(v || '').trim().length > 0);
  if (!hasContent) {
    return {
      language: 'en',
      translated: payload
    };
  }

  const prompt = `
Detect the primary language used in the user feedback JSON and translate each value to English.
Return ONLY valid JSON in this exact shape:
{
  "language": "<iso-code>",
  "translated": {
    "general": "...",
    "s01": "...",
    "s02": "...",
    "s03": "...",
    "s04": "...",
    "s05": "...",
    "s06": "...",
    "s07": "..."
  }
}

Rules:
- Keep keys exactly as provided.
- If a value is empty, keep it empty.
- Preserve meaning; do not summarize.

INPUT:
${JSON.stringify(payload)}
`;

  try {
    const text = await geminiGenerateText(prompt);
    const parsed = extractJsonFromGeminiText(text);
    const translated = FACT_CHECK_SECTION_KEYS.reduce((acc, key) => {
      const v = parsed?.translated?.[key];
      acc[key] = typeof v === 'string' ? v : (payload[key] || '');
      return acc;
    }, {});
    return {
      language: typeof parsed?.language === 'string' && parsed.language.trim() ? parsed.language.trim().toLowerCase() : 'en',
      translated
    };
  } catch (err) {
    console.warn('backTranslateFactCheckFeedback fallback to source:', err);
    return {
      language: 'unknown',
      translated: payload
    };
  }
}

/** Staff-only silo: internal precis + accreditor contact log (not on school-readable `reports/{sid}` root). */
function reportStaffWorkspaceRef(schoolId) {
  return doc(db, 'reports', schoolId, 'staff_workspace', 'data');
}

/** Strip staff-only fields before school-facing archive / benchmark snapshots. */
function stripStaffOnlyFromReportSnapshot(reportData) {
  if (!reportData || typeof reportData !== 'object') return reportData;
  const out = { ...reportData };
  delete out.internalNotes;
  if (out.meta && typeof out.meta === 'object') {
    const { accreditorContactLog: _omit, ...restMeta } = out.meta;
    out.meta = restMeta;
  }
  return out;
}

async function getAssignedAccreditorIdentity(auditorUid, fallbackName = '') {
  const uid = String(auditorUid || '').trim();
  const safeFallbackName = String(fallbackName || '').trim();
  if (!uid) return null;
  try {
    const staffSnap = await getDoc(doc(db, 'staff', uid));
    if (!staffSnap.exists()) {
      return {
        accreditorUid: uid,
        accreditorName: safeFallbackName,
        accreditorQuals: '',
        accreditorBio: '',
      };
    }
    const d = staffSnap.data() || {};
    const p = d.profile || {};
    const name =
      (d.displayName && String(d.displayName).trim()) ||
      (d.email && String(d.email).trim()) ||
      safeFallbackName;
    return {
      accreditorUid: uid,
      accreditorName: name,
      accreditorQuals:
        p.qualifications != null && String(p.qualifications).trim() !== ''
          ? p.qualifications
          : '',
      accreditorBio:
        p.bio != null && String(p.bio).trim() !== ''
          ? p.bio
          : '',
    };
  } catch (e) {
    console.warn('getAssignedAccreditorIdentity:', e);
    return {
      accreditorUid: uid,
      accreditorName: safeFallbackName,
      accreditorQuals: '',
      accreditorBio: '',
    };
  }
}

function applyAssignedAccreditorToReportData(reportData, accreditor) {
  if (!reportData || typeof reportData !== 'object' || !accreditor) return reportData;
  return {
    ...reportData,
    meta: {
      ...(reportData.meta || {}),
      authorUid: accreditor.accreditorUid || null,
    },
    certification: {
      ...(reportData.certification || {}),
      ...accreditor,
    },
  };
}

async function syncLiveReportAccreditorFromAssignment(schoolId, auditorUid, auditorName) {
  const reportRef = doc(db, 'reports', schoolId);
  const reportSnap = await getDoc(reportRef);
  if (!reportSnap.exists()) return false;
  const accreditor = await getAssignedAccreditorIdentity(auditorUid, auditorName);
  if (!accreditor) return false;
  await updateDoc(reportRef, {
    'meta.authorUid': accreditor.accreditorUid || null,
    'certification.accreditorUid': accreditor.accreditorUid || null,
    'certification.accreditorName': accreditor.accreditorName || '',
    'certification.accreditorQuals': accreditor.accreditorQuals || '',
    'certification.accreditorBio': accreditor.accreditorBio || '',
    'meta.lastEditedAt': serverTimestamp(),
  });
  return true;
}

/** Snap lead accreditor identity from the current assignment onto the report snapshot before archive (school-facing Â§06). */
async function mergeCertificationFromLeadStaff(reportData, schoolData) {
  const uid = schoolData?.assignedAuditorUid || reportData?.meta?.authorUid;
  const fallbackName = schoolData?.assignedAuditorName || reportData?.certification?.accreditorName || '';
  const accreditor = await getAssignedAccreditorIdentity(uid, fallbackName);
  return applyAssignedAccreditorToReportData(reportData, accreditor);
}

/** Empty live `reports/{schoolId}` shape for a new cycle (does not touch `schools/` workflow). */
export const buildInitialReportPayload = () => ({
  meta: {
    authorUid: null,
    generatedAt: serverTimestamp(),
    version: "2.5",
    isPublished: false,
    includeSurveyInReport: false,
    refinery: {
      evidence: { step: 1, label: 'uploaded' },
      survey: { step: 0, label: 'waiting' }
    }
  },
  institutionalBrief: {
    frontPage: { branding: "Inclusion 2.0", sealIssued: false },
    foundationsScorecard: {},
    executiveSummary: ""
  },
  maturityMatrix: {
    heatmapData: {},
    benchmarking: { percentile: null, region: "" }
  },
  stationAnalysis: {},
  evidencePortfolio: {
    registry: [],
    gaps: ""
  },
  strategicRoadmap: {
    content: "",
    nextPhase: "",
    resources: ""
  },
  certification: {
    expiryDate: null,
    accreditorUid: null,
    accreditorName: "",
    accreditorQuals: "",
    accreditorBio: "",
    digitalHash: "",
    peerReviewSigned: false,
    peerReviewerUid: null,
    peerReviewerName: null
  },
  approvals: {}
});

export const FINANCE_DEFAULT_PAY_RATES_BY_ROLE = {
  ACCREDITOR: { accreditorFeeGbp: 400, adminFeeGbp: 50, adminFeeRecipientUid: 'self' },
  SENIOR_ACCREDITOR: { accreditorFeeGbp: 500, adminFeeGbp: 50, adminFeeRecipientUid: 'self' },
  SUPER_ADMIN: { accreditorFeeGbp: 500, adminFeeGbp: 50, adminFeeRecipientUid: 'self' },
};

export function payRatesForRole(role) {
  const base =
    FINANCE_DEFAULT_PAY_RATES_BY_ROLE[role] ||
    FINANCE_DEFAULT_PAY_RATES_BY_ROLE.ACCREDITOR;
  return {
    ...base,
    effectiveFrom: new Date().toISOString().slice(0, 10),
  };
}

export const STAFF_PERMISSION_DEFINITIONS = [
  { key: 'canViewRegistry', label: 'Send portal magic link', description: 'Email the school their portal access link from the registry.' },
  { key: 'canViewAllSchools', label: 'See all schools in registry', description: 'Show every paid school in the registry. Without this, only schools assigned to you as lead accreditor appear.' },
  { key: 'canTogglePortal', label: 'Portal access & SEF return', description: 'Open/close the school portal, return the SEF for editing, and pull reports back to drafting.' },
  { key: 'canRunAI', label: 'Run AI engine', description: 'Trigger evidence analysis and AI report draft synthesis.' },
  { key: 'canDraftReport', label: 'Edit & draft reports', description: 'Edit live report content and submit amended drafts for internal QA.' },
  { key: 'canPeerReview', label: 'Peer review sign-off', description: 'Sign off peer review, handle post-appeal QA, and finalise when paired with publish access.' },
  { key: 'canPublish', label: 'Release & publish reports', description: 'Release drafts to schools for fact-check and run publish/finalise workflow steps.' },
  { key: 'canManagePayments', label: 'Fees, offers & payments', description: 'Edit the fee matrix and registration offers; confirm partner external payments.' },
  { key: 'canManagePublicShowcase', label: 'Public showcase & directory', description: 'Curate flagship schools and publish accredited schools to the corporate website feed.' },
  { key: 'canManagePortalLanguages', label: 'Portal languages', description: 'Activate AI-supported languages for the school portal.' },
  { key: 'canUseSupportAssistant', label: 'Support Assistant', description: 'Open the Support Assistant workspace and admin Help launcher.' },
  { key: 'canViewAuditTrail', label: 'System audit trail', description: 'View the append-only system logs tab.' },
  { key: 'canManageStaff', label: 'Staff, roles & invites', description: 'Invite and manage staff, assign lead auditors, use Email Checker, and override self-review conflicts.' },
  { key: 'canEditFramework', label: 'SEF framework strings (reserved)', description: 'Reserved for future framework-matrix editing; not wired to UI yet.' },
  { key: 'canPurgeSchool', label: 'Purge school data', description: 'Permanently delete selected school data from the registry (irreversible).' },
];

export const STAFF_PERMISSION_KEYS = STAFF_PERMISSION_DEFINITIONS.map((d) => d.key);

export const ROLE_PRESETS = {
  ACCREDITOR: {
    canViewRegistry: true,
    canViewAllSchools: false,
    canTogglePortal: false,
    canRunAI: false,
    canDraftReport: true,
    canPeerReview: false,
    canPublish: false,
    canManagePayments: false,
    canManagePublicShowcase: false,
    canManagePortalLanguages: false,
    canUseSupportAssistant: false,
    canManageStaff: false,
    canEditFramework: false,
    canViewAuditTrail: false,
    canPurgeSchool: false
  },
  SENIOR_ACCREDITOR: {
    canViewRegistry: true,
    canViewAllSchools: true,
    canTogglePortal: true,
    canRunAI: true,
    canDraftReport: true,
    canPeerReview: true,
    canPublish: true,
    canManagePayments: true,
    canManagePublicShowcase: true,
    canManagePortalLanguages: true,
    canUseSupportAssistant: true,
    canManageStaff: false,
    canEditFramework: false,
    canViewAuditTrail: false,
    canPurgeSchool: false
  },
  SUPER_ADMIN: {
    canViewRegistry: true,
    canViewAllSchools: true,
    canTogglePortal: true,
    canRunAI: true,
    canDraftReport: true,
    canPeerReview: true,
    canPublish: true,
    canManagePayments: true,
    canManagePublicShowcase: true,
    canManagePortalLanguages: true,
    canUseSupportAssistant: true,
    canManageStaff: true,
    canEditFramework: true,
    canViewAuditTrail: true,
    canPurgeSchool: true
  }
};

export const adminServices = {
  // New: Prioritized Email Lookup Utility
  getSchoolEmail: (schoolData) => {
    return schoolData?.officialEmail || schoolData?.answers?.officialEmail || schoolData?.answers?.q3 || null;
  },

  // New: Registry Healing (One-time migration for existing records)
  healRegistry: async (adminName) => {
    const { getDocs, collection } = await import('firebase/firestore');
    const schoolsRef = collection(db, "schools");
    const snap = await getDocs(schoolsRef);
    let count = 0;
    
    for (const schoolDoc of snap.docs) {
      const data = schoolDoc.data();
      if (!data.officialEmail) {
        const email = data.answers?.officialEmail || data.answers?.q3;
        if (email) {
          await updateDoc(schoolDoc.ref, { officialEmail: email });
          count++;
        }
      }
    }
    await adminServices.logAction(adminName, "REGISTRY_HEALING", { updatedCount: count });
    return count;
  },

  // New: Auth Methods
  login: async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ hd: "ifip.group" }); // Suggest IFIP domain
    return signInWithPopup(auth, provider);
  },

  logout: () => signOut(auth),

  syncStaffMember: async (user) => {
    const normalizedEmail = (user?.email || "").toLowerCase();
    if (!normalizedEmail.endsWith('@ifip.group')) {
      throw new Error("Access restricted to @ifip.group accounts.");
    }

    const staffRef = doc(db, "staff", user.uid);
    let snap = await getDoc(staffRef);
    let staffData = null;

    // 1. Handle UID-linked or Email-linked records
    if (snap.exists()) {
      staffData = snap.data();
    } else {
      // Legacy docs used staff/{email} as the id. Rules only allow read when path id == auth.uid,
      // so this lookup fails for normal Google UIDs — must not block new staff shell creation.
      try {
        const emailRef = doc(db, "staff", normalizedEmail);
        const emailSnap = await getDoc(emailRef);

        if (emailSnap.exists()) {
          staffData = {
            ...emailSnap.data(),
            uid: user.uid,
            email: normalizedEmail,
            displayName: user.displayName || emailSnap.data().displayName || normalizedEmail,
            pendingInvite: false,
          };
          if (emailSnap.data().pendingInvite === true && emailSnap.data().isApproved === true) {
            try {
              await adminServices.dismissStaffApprovalNotifications(user.uid);
            } catch (dismissErr) {
              console.warn('dismissStaffApprovalNotifications skipped:', dismissErr);
            }
          }
          await setDoc(staffRef, staffData, { merge: true });
          if (emailSnap.data().pendingInvite === true && !staffData.isApproved) {
            try {
              await adminServices.createNotification('PERM_canManageStaff', user.uid, {
                label: `Approve invited staff member: ${staffData.displayName || normalizedEmail}`,
                priority: 'red',
                category: 'staff',
                type: 'STAFF_APPROVAL',
                isAction: true,
                actionPath: '/admin?tab=staff',
              });
            } catch (notifyErr) {
              console.warn('Invited staff approval notification skipped:', notifyErr);
            }
          }
          try {
            await deleteDoc(emailRef);
          } catch (deleteErr) {
            console.warn('Legacy staff email doc cleanup skipped:', deleteErr);
          }
          snap = await getDoc(staffRef);
        }
      } catch (legacyErr) {
        console.warn('Legacy staff email lookup skipped:', legacyErr);
      }
    }

    // 2. BOOTSTRAP LOGIC: ensure role presets backfill missing permission keys.
    if (staffData && staffData.role) {
      const preset = ROLE_PRESETS[staffData.role] || {};
      const existing = staffData.permissions || {};
      const mergedPermissions = { ...preset, ...existing };
      const needsPermissionBackfill =
        !staffData.permissions ||
        Object.keys(staffData.permissions).length === 0 ||
        Object.keys(preset).some((k) => !(k in existing));
      if (needsPermissionBackfill) {
        staffData.permissions = mergedPermissions;
        await updateDoc(staffRef, { permissions: mergedPermissions });

        const isApprovedStaff = staffData.isApproved === true;
        if (isApprovedStaff) {
          // Preset keys were added in code — merge silently; do not re-open onboarding approval.
          try {
            await adminServices.dismissStaffApprovalNotifications(user.uid);
          } catch (dismissErr) {
            console.warn('dismissStaffApprovalNotifications skipped:', dismissErr);
          }
        } else {
          try {
            await adminServices.createNotification('PERM_canManageStaff', user.uid, {
              label: `Approve new staff member: ${user.displayName}`,
              priority: 'red',
              category: 'staff',
              type: 'STAFF_APPROVAL',
              isAction: true,
              actionPath: '/admin?tab=staff',
            });
          } catch (notifyErr) {
            // Notification write can be blocked for non-approved users; never block auth sync.
            console.warn('Staff approval notification skipped:', notifyErr);
          }
        }
      }
    }

    // 3. Fallback: Create new unauthorized shell if no data found
    if (!staffData) {
      staffData = {
        uid: user.uid,
        email: normalizedEmail,
        displayName: user.displayName,
        isApproved: false,
        role: null,
        permissions: {},
        profile: { bio: "", qualifications: "", experience: "" },
        joinedAt: serverTimestamp()
      };
      await setDoc(staffRef, staffData);
    }

    return staffData;
  },

  updateProfile: async (uid, profileData, userName) => {
    const staffRef = doc(db, "staff", uid);
    await updateDoc(staffRef, { 
      "pendingUpdate": profileData,
      "lastProfileUpdateRequested": serverTimestamp() 
    });
    await adminServices.logAction(userName, "REQUEST_PROFILE_UPDATE", { uid });
  },

   getGlobalTasks: async (user, permissions) => {
    const { getDocs, collection, query, where } = await import('firebase/firestore');
    const tasks = [];

    // 1. Staff & Profiles (Super Admin / Staff Manager)
    if (permissions.canManageStaff) {
      const staffRef = collection(db, "staff");
      const unapproved = await getDocs(query(staffRef, where("isApproved", "==", false)));
      unapproved.forEach(doc => tasks.push({
        type: 'STAFF_APPROVAL',
        priority: 'red',
        label: `Approve Access: ${doc.data().displayName}`,
        id: doc.id,
        category: 'staff'
      }));

      const updates = await getDocs(query(staffRef, where("pendingUpdate", "!=", null)));
      updates.forEach(doc => tasks.push({
        type: 'PROFILE_REVIEW',
        priority: 'amber',
        label: `Review Profile: ${doc.data().displayName}`,
        id: doc.id,
        category: 'staff'
      }));
    }

    // 2. Aggregate Data for Schools & Reports
    const schoolsRef = collection(db, "schools");
    const reportsRef = collection(db, "reports");
    const [schoolSnap, reportSnap] = await Promise.all([getDocs(schoolsRef), getDocs(reportsRef)]);
    
    const reportsMap = {};
    reportSnap.forEach(r => reportsMap[r.id] = r.data());

    schoolSnap.forEach(schoolDoc => {
      const school = schoolDoc.data();
      if (!['paid', 'awaiting_external_settlement'].includes(school.paymentStatus)) return;
      const sId = schoolDoc.id;
      const report = reportsMap[sId];
      const step = school.workflowStep;
      
      // Activation & Assignment
      if (school.status === 'inactive' && permissions.canTogglePortal) {
        tasks.push({ type: 'ACTIVATE_SCHOOL', priority: 'red', label: `Activate school access: ${school.officialName}`, id: sId, category: 'registry', isAction: true, actionPath: `/admin?tab=registry&schoolId=${sId}` });
      }
      if (!school.assignedAuditorUid && permissions.canManageStaff) {
        tasks.push({ type: 'ASSIGN_AUDITOR', priority: 'red', label: `Assign lead accreditor: ${school.officialName}`, id: sId, category: 'registry', isAction: true, actionPath: `/admin?tab=registry&schoolId=${sId}` });
      }

      // Workflow Logic
      const reportMeta = reportsMap[sId]?.meta;
      if (step === 'peer_review' && permissions.canPeerReview && school.assignedAuditorUid !== user.uid) {
        const postAppeal = isPostAppealQa(reportMeta);
        tasks.push({
          type: postAppeal ? 'APPEAL_QA_REVIEW' : 'PEER_REVIEW',
          priority: 'red',
          label: postAppeal
            ? `Post-appeal QA decision required: ${school.officialName}`
            : `Internal review sign-off required: ${school.officialName}`,
          id: sId,
          category: 'reports',
          isAction: true,
          actionPath: `/admin?tab=registry&schoolId=${sId}&mode=report`,
        });
      }
      if (step === 'internal_review_rejected' && school.assignedAuditorUid === user.uid) {
        tasks.push({ type: 'REVISION_REQUIRED', priority: 'red', label: `Revisions required after internal review: ${school.officialName}`, id: sId, category: 'reports', isAction: true, actionPath: `/admin?tab=registry&schoolId=${sId}&mode=report` });
      }
      if (step === 'appeals' && (permissions.canPublish || permissions.canManageStaff)) {
        tasks.push({ type: 'APPEAL_RESPONSE', priority: 'red', label: `School submitted fact-check feedback: ${school.officialName}`, id: sId, category: 'reports', isAction: true, actionPath: `/admin?tab=registry&schoolId=${sId}&mode=report` });
      }

      // AI/Refinery Context
      const refineryStep = report?.meta?.refinery?.evidence?.step || 0;
      if (refineryStep === 1 && permissions.canRunAI) {
        tasks.push({ type: 'RUN_AI', priority: 'amber', label: `Run AI evidence analysis: ${school.officialName}`, id: sId, category: 'registry', isAction: true, actionPath: `/admin?tab=registry&schoolId=${sId}` });
      }
    });

    return tasks.sort((a, b) => (a.priority === 'red' ? -1 : 1));
  },
  
  // NEW: Generates a structured snapshot of the Action Center for Email Digests
  getTaskDigestSummary: async (user, permissions) => {
    const tasks = await adminServices.getGlobalTasks(user, permissions);
    
    // Group tasks by priority for the email template
    // Wording/Templates for Admin Communications
    const emailTemplates = {
      subjects: {
        IMMEDIATE: "IFIP ACTION REQUIRED: {{label}}",
        DIGEST: "IFIP Daily Brief: {{count}} tasks pending in your Action Center"
      },
      taskDescriptions: {
        STAFF_APPROVAL: "A new team member has registered and requires identity verification before access is granted.",
        PROFILE_REVIEW: "An accreditor has updated their professional profile; please review and publish the changes.",
        AUDITOR_ASSIGNED: "You have been assigned as an accreditor for this institution. Please visit the Accreditation Portal to review the SEF and begin your review.",
        PEER_REVIEW: "A report draft has been submitted for peer review. Your clinical oversight is required.",
        REVISION_REQUIRED: "Your report draft has been returned with peer review feedback. Revisions are needed before publication.",
        APPEAL_RESPONSE: "A school has submitted accuracy feedback or an appeal regarding their report.",
        ACTIVATE_SCHOOL: "Payment has been verified. This school is now ready for manual portal activation.",
        ASSIGN_AUDITOR: "A new school is in the registry but has no Lead Auditor assigned.",
        RUN_AI: "The evidence vault is full. The AI Refinery is waiting for you to trigger the extraction phase."
      },
      footer: "This is an automated system notification from the IFIP Accreditation Hub. Please reply to info@ifip.group for support."
    };

    // Group tasks by priority for the email template
    const summary = {
      templates: emailTemplates, // Included so backend can pull the correct wording
      urgent: tasks.filter(t => t.priority === 'red'),
      standard: tasks.filter(t => t.priority === 'amber'),
      portalLink: `${window.location.origin}/admin`,
      totalCount: tasks.length,
      timestamp: new Date().toISOString()
    };

    return summary;
  },

  getAllStaff: async () => {
    const { getDocs, query, orderBy } = await import('firebase/firestore');
    const staffRef = collection(db, "staff");
    const q = query(staffRef, orderBy("joinedAt", "desc"));
    const snapshot = await getDocs(q);
    const all = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
    const emailsWithUidAccount = new Set(
      all
        .filter((row) => row.uid && row.email)
        .map((row) => String(row.email).toLowerCase())
    );
    return all.filter((row) => {
      const rowEmail = String(row.email || row.id || '').toLowerCase();
      const isEmailKeyedDoc = row.id.includes('@');
      if (isEmailKeyedDoc && emailsWithUidAccount.has(rowEmail)) return false;
      return true;
    });
  },

  inviteStaffMember: async ({ email, displayName, role, isApproved = false }, adminName) => {
    const normalizedEmail = String(email || '').trim().toLowerCase();
    if (!normalizedEmail.endsWith('@ifip.group')) {
      throw new Error('Staff email must be an @ifip.group address.');
    }
    if (!role || !ROLE_PRESETS[role]) {
      throw new Error('Select a role template for the invitation.');
    }

    const { getDocs, query, where } = await import('firebase/firestore');
    const byEmailQuery = query(collection(db, 'staff'), where('email', '==', normalizedEmail));
    const byEmailSnap = await getDocs(byEmailQuery);
    if (!byEmailSnap.empty) {
      throw new Error('A staff record already exists for this email.');
    }

    const inviteRef = doc(db, 'staff', normalizedEmail);
    const inviteSnap = await getDoc(inviteRef);
    if (inviteSnap.exists()) {
      throw new Error('An invitation is already pending for this email.');
    }

    const permissions = { ...ROLE_PRESETS[role] };
    const payRates = payRatesForRole(role);
    await setDoc(inviteRef, {
      email: normalizedEmail,
      displayName: String(displayName || '').trim() || normalizedEmail.split('@')[0],
      isApproved: !!isApproved,
      role,
      permissions,
      payRates,
      profile: { bio: '', qualifications: '', experience: '' },
      pendingInvite: true,
      invitedAt: serverTimestamp(),
      invitedBy: adminName,
      joinedAt: serverTimestamp(),
    });

    await adminServices.logAction(adminName, 'STAFF_INVITE_CREATED', {
      email: normalizedEmail,
      role,
      isApproved: !!isApproved,
    });

    return { email: normalizedEmail, role, isApproved: !!isApproved };
  },

  updateStaffMember: async (uid, data, adminName) => {
    const staffRef = doc(db, "staff", uid);
    const before = await getDoc(staffRef);
    const beforeApproved = before.exists() ? !!before.data()?.isApproved : false;
    await updateDoc(staffRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
    await adminServices.logAction(adminName, "UPDATE_STAFF_PERMISSIONS", { 
      targetUid: uid, 
      newRole: data.role,
      isApproved: data.isApproved 
    });

    if (data.isApproved === true && beforeApproved === false) {
      const after = await getDoc(staffRef);
      const displayName = after.exists() ? (after.data()?.displayName || uid) : uid;
      const actor = actorUid();
      try {
        await adminServices.dismissStaffApprovalNotifications(uid);
      } catch (dismissErr) {
        console.warn('dismissStaffApprovalNotifications skipped:', dismissErr);
      }
      await broadcastAdminResolutionNotice({
        excludeUid: actor,
        label: `${adminName} approved staff access for ${displayName}`,
        predicate: (s) => !!s.permissions?.canManageStaff || s.role === 'SUPER_ADMIN'
      });
    }
  },

  createSupportIssue: async ({
    issueType,
    attemptedAction,
    errorSummary,
    description,
    sourceSurface,
    route,
    schoolId = null,
    schoolName = null,
    reportId = null,
    workflowStep = null,
    qaPhase = null,
    language = null,
    reporter = null,
    reporterPermissions = null,
    context = null,
  }) => {
    const payload = {
      supportVersion: 1,
      status: 'open',
      issueType: sanitizeSupportIssueEnum(issueType, ['ui', 'permissions', 'workflow', 'email', 'other'], 'other'),
      sourceSurface: sanitizeSupportIssueText(sourceSurface, 120) || 'unknown',
      route: sanitizeSupportIssueText(route, 300),
      schoolId: sanitizeSupportIssueText(schoolId, 120) || null,
      schoolName: sanitizeSupportIssueText(schoolName, 240) || null,
      reportId: sanitizeSupportIssueText(reportId, 120) || null,
      workflowStep: sanitizeSupportIssueText(workflowStep, 120) || null,
      qaPhase: sanitizeSupportIssueText(qaPhase, 120) || null,
      language: sanitizeSupportIssueText(language, 40) || null,
      attemptedAction: sanitizeSupportIssueText(attemptedAction, 500),
      errorSummary: sanitizeSupportIssueText(errorSummary, 1000),
      description: sanitizeSupportIssueText(description, 5000),
      browser: {
        userAgent: sanitizeSupportIssueText(
          typeof navigator !== 'undefined' ? navigator.userAgent : '',
          600
        ),
        language: sanitizeSupportIssueText(
          typeof navigator !== 'undefined' ? navigator.language : '',
          40
        ),
      },
      reporter: {
        uid: sanitizeSupportIssueText(reporter?.uid, 120) || null,
        email: sanitizeSupportIssueText(reporter?.email, 240) || null,
        displayName: sanitizeSupportIssueText(reporter?.displayName, 240) || 'Unknown reporter',
        actorType: sanitizeSupportIssueText(reporter?.actorType, 80) || 'unknown',
        role: sanitizeSupportIssueText(reporter?.role, 120) || null,
      },
      reporterPermissions: sanitizeSupportIssueContext(reporterPermissions),
      context: sanitizeSupportIssueContext(context),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, 'supportIssues'), payload);

    try {
      if (reporter?.actorType === 'staff') {
        await adminServices.logAction(reporter?.displayName || reporter?.email || 'IFIP Staff', 'SUPPORT_ISSUE_CREATED', {
          issueId: docRef.id,
          issueType: payload.issueType,
          sourceSurface: payload.sourceSurface,
        });
      }
    } catch (err) {
      console.warn('Support issue log skipped:', err);
    }

    return docRef.id;
  },

  updateSupportIssue: async (issueId, patch, actorName = 'IFIP Staff') => {
    const issueRef = doc(db, 'supportIssues', issueId);
    const nextStatus = sanitizeSupportIssueEnum(
      patch?.status,
      ['open', 'triaged', 'awaiting_reply', 'resolved'],
      'open'
    );

    await updateDoc(issueRef, {
      status: nextStatus,
      handledBy: sanitizeSupportIssueContext(patch?.handledBy),
      updatedAt: serverTimestamp(),
      ...(nextStatus === 'resolved' ? { resolvedAt: serverTimestamp() } : { resolvedAt: null }),
    });

    try {
      await adminServices.logAction(actorName, 'SUPPORT_ISSUE_UPDATED', {
        issueId,
        status: nextStatus,
      });
    } catch (err) {
      console.warn('Support issue update log skipped:', err);
    }
  },

  approveProfileUpdate: async (uid, approvedData, adminName) => {
    const staffRef = doc(db, "staff", uid);
    const before = await getDoc(staffRef);
    const displayName = before.exists() ? (before.data()?.displayName || uid) : uid;
    await updateDoc(staffRef, {
      profile: approvedData,
      pendingUpdate: null,
      lastProfileUpdateRequested: null
    });
    await adminServices.logAction(adminName, "APPROVE_PROFILE_UPDATE", { targetUid: uid });

    const actor = actorUid();
    await broadcastAdminResolutionNotice({
      excludeUid: actor,
      label: `${adminName} published profile updates for ${displayName}`,
      predicate: (s) => !!s.permissions?.canManageStaff || s.role === 'SUPER_ADMIN'
    });
  },

  rejectProfileUpdate: async (uid, adminName) => {
    const staffRef = doc(db, "staff", uid);
    const before = await getDoc(staffRef);
    const displayName = before.exists() ? (before.data()?.displayName || uid) : uid;
    await updateDoc(staffRef, {
      pendingUpdate: null,
      lastProfileUpdateRequested: null
    });
    await adminServices.logAction(adminName, "REJECT_PROFILE_UPDATE", { targetUid: uid });

    const actor = actorUid();
    await broadcastAdminResolutionNotice({
      excludeUid: actor,
      label: `${adminName} rejected profile update request for ${displayName}`,
      predicate: (s) => !!s.permissions?.canManageStaff || s.role === 'SUPER_ADMIN'
    });
  },

  getSystemAlerts: async () => {
    const { getDocs, collection, query, where } = await import('firebase/firestore');
    const staffRef = collection(db, "staff");
    const unapproved = await getDocs(query(staffRef, where("isApproved", "==", false)));
    const updates = await getDocs(query(staffRef, where("pendingUpdate", "!=", null)));
    return {
      pendingStaffCount: unapproved.size,
      pendingProfilesCount: updates.size
    };
  },

  getApprovedStaff: async () => {
    const { getDocs, query, where } = await import('firebase/firestore');
    const staffRef = collection(db, "staff");
    const q = query(staffRef, where("isApproved", "==", true));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  /** Broadcast one notification payload to a set of internal IFIP staff roles. */
  notifyRoles: async (roles, linkId, data, options = {}) => {
    const exclude = new Set(options.excludeUids || []);
    const staff = await adminServices.getApprovedStaff();
    const targets = staff.filter(s => roles.includes(s.role) && s.id && !exclude.has(s.id));
    await Promise.all(targets.map(s => adminServices.createNotification(s.id, linkId, data)));
  },

  assignAuditor: async (schoolId, auditorUid, auditorName, assignerName = 'IFIP Staff') => {
    const schoolRef = doc(db, "schools", schoolId);
    await updateDoc(schoolRef, {
      assignedAuditorUid: auditorUid,
      assignedAuditorName: auditorName,
      lastAssignmentUpdate: serverTimestamp()
    });
    await syncLiveReportAccreditorFromAssignment(schoolId, auditorUid, auditorName);

    const schoolSnap = await getDoc(schoolRef);
    const schoolData = schoolSnap.exists() ? schoolSnap.data() : {};
    const ctx = schoolEmailContext(schoolData, schoolId);
    await adminServices.createNotification(auditorUid, schoolId, {
      label: `You have been assigned as lead accreditor for ${ctx.schoolName}`,
      priority: 'red',
      category: 'registry',
      type: 'AUDITOR_ASSIGNED',
      isAction: true,
      actionPath: `/admin?tab=registry&schoolId=${schoolId}`,
      forceEmail: true,
      emailTemplateKey: 'email_staff_accreditor_assigned',
      emailTemplateData: {
        ...ctx,
        assignerName: assignerName || auth.currentUser?.displayName || 'IFIP Staff',
      },
    });

    const actor = actorUid();
    const actorName = auth.currentUser?.displayName || 'IFIP Staff';
    await broadcastAdminResolutionNotice({
      excludeUid: actor,
      label: `${actorName} assigned lead accreditor for ${ctx.schoolName}`,
      predicate: (s) => !!s.permissions?.canManageStaff || s.role === 'SUPER_ADMIN'
    });
  },

  assignPeerReviewer: async (schoolId, reviewerUid, reviewerName, assignerName = 'IFIP Staff') => {
    const schoolRef = doc(db, 'schools', schoolId);
    const reportRef = doc(db, 'reports', schoolId);
    await updateDoc(schoolRef, {
      assignedPeerReviewerUid: reviewerUid,
      assignedPeerReviewerName: reviewerName,
      lastPeerReviewerAssignmentAt: serverTimestamp(),
    });
    const reportSnap = await getDoc(reportRef);
    if (reportSnap.exists()) {
      await updateDoc(reportRef, {
        'certification.peerReviewerUid': reviewerUid,
        'certification.peerReviewerName': reviewerName,
        'certification.peerReviewSigned': false,
      });
    }

    const schoolSnap = await getDoc(schoolRef);
    const schoolData = schoolSnap.exists() ? schoolSnap.data() : {};
    const ctx = schoolEmailContext(schoolData, schoolId);
    await adminServices.createNotification(reviewerUid, schoolId, {
      label: `You have been assigned as second reviewer for ${ctx.schoolName}`,
      priority: 'red',
      category: 'reports',
      type: 'PEER_REVIEWER_ASSIGNED',
      isAction: true,
      actionPath: `/admin?tab=registry&schoolId=${schoolId}&mode=report`,
      forceEmail: true,
      emailTemplateKey: 'email_staff_second_reviewer_assigned',
      emailTemplateData: {
        ...ctx,
        assignerName: assignerName || auth.currentUser?.displayName || 'IFIP Staff',
      },
    });
  },

  notifySefSubmitted: async (schoolId) => {
    const schoolSnap = await getDoc(doc(db, 'schools', schoolId));
    if (!schoolSnap.exists()) return;
    const schoolData = schoolSnap.data();
    const ctx = schoolEmailContext(schoolData, schoolId);
    const payload = {
      label: `SEF submitted: ${ctx.schoolName}`,
      priority: 'red',
      category: 'registry',
      type: 'SEF_SUBMITTED',
      isAction: true,
      actionPath: `/admin?tab=registry&schoolId=${schoolId}`,
      forceEmail: true,
      emailTemplateKey: 'email_staff_sef_submitted',
      emailTemplateData: ctx,
    };
    const uids = new Set();
    if (schoolData.assignedAuditorUid) uids.add(schoolData.assignedAuditorUid);
    const staff = await adminServices.getApprovedStaff();
    staff.filter((s) => s.role === 'SUPER_ADMIN').forEach((s) => uids.add(s.id));
    await Promise.all([...uids].map((uid) => adminServices.createNotification(uid, schoolId, payload)));
  },

  // 1. Activate a school and set the start date
  activateSchool: async (schoolId, schoolName, adminName) => {
    const schoolRef = doc(db, "schools", schoolId);
    await updateDoc(schoolRef, {
      status: 'active',
      activatedAt: serverTimestamp(),
      paymentStatus: 'paid'
    });
    
    // Log the event
    await adminServices.logAction(adminName, "ACTIVATE_SCHOOL", { schoolId, schoolName });

    const actor = actorUid();
    await broadcastAdminResolutionNotice({
      excludeUid: actor,
      label: `${adminName} activated portal access for ${schoolName || schoolId}`,
      predicate: (s) => !!s.permissions?.canTogglePortal || s.role === 'SUPER_ADMIN' || s.role === 'SENIOR_ACCREDITOR'
    });
  },

  // 2. Generate the Magic Link
  getMagicLink: (schoolId) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/portal?sid=${schoolId}`;
  },

  /**
   * One-time: move legacy `internalNotes` / `meta.accreditorContactLog` off the school-readable report doc
   * into `reports/{sid}/staff_workspace/data` (staff-only rules), then delete those fields from the report.
   */
  migrateReportStaffWorkspaceFromLegacyIfNeeded: async (schoolId) => {
    const reportRef = doc(db, 'reports', schoolId);
    const wsRef = reportStaffWorkspaceRef(schoolId);
    const [wsSnap, reportSnap] = await Promise.all([getDoc(wsRef), getDoc(reportRef)]);
    if (wsSnap.exists()) return false;
    if (!reportSnap.exists()) return false;
    const r = reportSnap.data() || {};
    const legacyNotes = r.internalNotes;
    const legacyLog = r.meta?.accreditorContactLog;
    const hasLegacy =
      (typeof legacyNotes === 'string' && legacyNotes.trim() !== '') ||
      (Array.isArray(legacyLog) && legacyLog.length > 0);
    if (!hasLegacy) return false;
    await setDoc(
      wsRef,
      {
        internalNotes: typeof legacyNotes === 'string' ? legacyNotes : '',
        accreditorContactLog: Array.isArray(legacyLog) ? legacyLog : [],
        migratedFromReportRootAt: serverTimestamp(),
      },
      { merge: true }
    );
    const patch = { 'meta.lastEditedAt': serverTimestamp() };
    if (legacyNotes !== undefined) patch.internalNotes = deleteField();
    if (legacyLog !== undefined) patch['meta.accreditorContactLog'] = deleteField();
    await updateDoc(reportRef, patch);
    return true;
  },

  /**
   * Append-only accreditor call / exchange notes on staff-only `reports/{schoolId}/staff_workspace/data`.
   */
  appendAccreditorContactLogEntry: async ({
    schoolId,
    schoolName,
    text,
    actorName,
    actorUid,
  }) => {
    const trimmed = String(text || '').trim();
    if (!trimmed) throw new Error('Note text is empty.');
    const wsRef = reportStaffWorkspaceRef(schoolId);
    const entry = {
      text: trimmed.slice(0, 8000),
      authorName: actorName || 'Staff',
      authorUid: actorUid || null,
      createdAt: Timestamp.now(),
    };
    await setDoc(
      wsRef,
      {
        accreditorContactLog: arrayUnion(entry),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    const reportRef = doc(db, 'reports', schoolId);
    await updateDoc(reportRef, { 'meta.lastEditedAt': serverTimestamp() });
    await adminServices.logAction(actorName || 'Staff', 'ACCREDITOR_CONTACT_LOG', {
      schoolId,
      schoolName: schoolName || schoolId,
      excerpt: entry.text.slice(0, 400),
    });
  },

  /**
   * Queue a localized portal Magic Link email to the school's lead email via `onSchoolWrite`
   * (fires on false â†’ true transition of `magicLinkRequested`).
   */
  requestSchoolPortalLinkEmail: async (schoolId, schoolName, actorName) => {
    const schoolRef = doc(db, 'schools', schoolId);
    const snap = await getDoc(schoolRef);
    if (!snap.exists()) throw new Error('School not found.');
    const data = snap.data();
    const to = adminServices.getSchoolEmail(data);
    if (!to) throw new Error('No institutional email on file for this school.');

    await updateDoc(schoolRef, { magicLinkRequested: false });
    await updateDoc(schoolRef, { magicLinkRequested: true });

    await adminServices.logAction(actorName || 'Staff', 'PORTAL_LINK_EMAIL_REQUESTED', {
      schoolId,
      schoolName: schoolName || data.officialName || schoolId,
      recipientEmail: to
    });
  },

  getRenewalEligibility: (schoolData) => {
    const workflowStep = String(schoolData?.workflowStep || '').toLowerCase();
    const cycleComplete = cycleIsFinalised(workflowStep);
    return {
      canRenew: cycleComplete,
      canUpgrade: cycleComplete,
      workflowStep
    };
  },

  startRenewalOrUpgradeRequest: async ({
    schoolId,
    requestType,
    targetLevel,
    billingLevel = '',
    feeGbp = null,
    schoolName,
    actorName = 'School Lead',
    submitDetails = null
  }) => {
    const schoolRef = doc(db, 'schools', schoolId);
    const schoolSnap = await getDoc(schoolRef);
    if (!schoolSnap.exists()) throw new Error('School not found.');

    const school = schoolSnap.data();
    const { canRenew } = adminServices.getRenewalEligibility(school);
    const type = requestType === 'upgrade_level' ? 'upgrade_level' : 'renew_same_level';
    if (!canRenew) {
      throw new Error('A renewal or upgrade can only start once the current cycle is finalised.');
    }

    const cycleRef = `${schoolId}_${Date.now()}`;
    const appliedLevel = String(school?.accreditationLevel || 'foundation').toLowerCase();
    const quoted = Number(feeGbp);
    await updateDoc(schoolRef, {
      renewalRequest: {
        requestType: type,
        targetLevel,
        billingLevel: billingLevel || targetLevel,
        appliedLevel,
        feeGbp: Number.isFinite(quoted) ? Math.round(quoted) : null,
        status: 'initiated',
        initiatedAt: serverTimestamp(),
        cycleRef,
        initiatedBy: actorName,
        ...(submitDetails && typeof submitDetails === 'object' ? { submitDetails } : {})
      },
      lastWorkflowUpdate: serverTimestamp()
    });

    // Lifecycle subcollection writes are staff-only in Firestore rules; school portal users
    // record the request on `schools/{sid}.renewalRequest` only.

    await adminServices.logAction(actorName, 'CYCLE_REQUEST_INITIATED', {
      schoolId,
      schoolName: schoolName || school?.officialName || schoolId,
      requestType: type,
      targetLevel,
      cycleRef
    });

    return { cycleRef, appliedLevel, requestType: type };
  },

  // 3. Initialize the 6-Section Phase 2 Report
  initializeReport: async (schoolId) => {
    const reportRef = doc(db, "reports", schoolId);
    const schoolRef = doc(db, "schools", schoolId);
    const schoolSnap = await getDoc(schoolRef);
    const schoolData = schoolSnap.exists() ? schoolSnap.data() : {};

    await updateDoc(schoolRef, { 
      workflowStep: 'submitted',
      lastWorkflowUpdate: serverTimestamp()
    });

    let initialReport = buildInitialReportPayload();
    const accreditor = await getAssignedAccreditorIdentity(
      schoolData?.assignedAuditorUid,
      schoolData?.assignedAuditorName
    );
    initialReport = applyAssignedAccreditorToReportData(initialReport, accreditor);
    initialReport.meta.accreditationLevel = normalizeAccreditationLevel(schoolData.accreditationLevel);

    await setDoc(reportRef, initialReport);
  },

  /**
   * Change this cycle's target / awarded tier (Foundation, Certified, Global).
   * Locked after finalise. Does not rewrite fees, payments, or registered signup level.
   */
  setSchoolAccreditationLevel: async (schoolId, nextLevel, user) => {
    const to = String(nextLevel || '').trim().toLowerCase();
    if (!isValidAccreditationLevel(to)) {
      throw new Error('Choose Foundation, Certified, or Global Accreditation.');
    }

    const schoolRef = doc(db, 'schools', schoolId);
    const reportRef = doc(db, 'reports', schoolId);
    const [schoolSnap, reportSnap] = await Promise.all([getDoc(schoolRef), getDoc(reportRef)]);
    if (!schoolSnap.exists()) throw new Error('School not found.');

    const schoolData = schoolSnap.data() || {};
    const step = String(schoolData.workflowStep || '').toLowerCase();
    if (step === 'finalised' || reportSnap.data()?.meta?.isPublished === true) {
      throw new Error('Accreditation level cannot be changed after the cycle is finalised.');
    }

    const from = normalizeAccreditationLevel(schoolData.accreditationLevel);
    const registered = resolveRegisteredAccreditationLevel(schoolData);
    const adminName = user?.displayName || user?.email || 'Staff';
    const schoolName = schoolData.officialName || schoolId;

    if (from === to) {
      if (!schoolData.registeredAccreditationLevel) {
        await updateDoc(schoolRef, { registeredAccreditationLevel: registered });
      }
      return { changed: false, from, to, registered };
    }

    const schoolPatch = {
      accreditationLevel: to,
      lastWorkflowUpdate: serverTimestamp(),
    };
    if (!schoolData.registeredAccreditationLevel) {
      schoolPatch.registeredAccreditationLevel = registered;
    }

    await updateDoc(schoolRef, schoolPatch);

    if (reportSnap.exists()) {
      await updateDoc(reportRef, {
        'meta.accreditationLevel': to,
        'meta.lastEditedAt': serverTimestamp(),
      });
    }

    const fromLabel = accreditationLevelLabel(from, { long: true });
    const toLabel = accreditationLevelLabel(to, { long: true });
    const lifecycleRef = collection(db, 'schools', schoolId, 'lifecycle');
    await addDoc(lifecycleRef, {
      type: 'ACCREDITATION_LEVEL_CHANGE',
      eventLabel: `Awarded tier changed from ${fromLabel} to ${toLabel}`,
      timestamp: serverTimestamp(),
      adminName,
      fromLevel: from,
      toLevel: to,
      registeredLevel: registered,
    });

    await adminServices.logAction(adminName, 'ACCREDITATION_LEVEL_CHANGE', {
      schoolId,
      schoolName,
      from,
      to,
      registered,
    });

    return { changed: true, from, to, registered };
  },

  // 4. Update the Specific Workflow Step
  updateWorkflowStep: async (schoolId, step, schoolName, adminName = "System") => {
    try {
      const schoolRef = doc(db, "schools", schoolId);
      const beforeSnap = await getDoc(schoolRef);
      const prevStep = beforeSnap.exists() ? beforeSnap.data()?.workflowStep : null;

      await updateDoc(schoolRef, {
        workflowStep: step,
        lastWorkflowUpdate: serverTimestamp()
      });

      await adminServices.logAction(adminName || "System", `WORKFLOW_${String(step || "").toUpperCase()}`, {
        schoolId,
        schoolName,
        from: prevStep || null,
        to: step || null
      });

      // If another admin cleared a shared queue state, notify peers who may have seen the same Action Center item.
      const actor = actorUid();
      const schoolLabel = schoolName || schoolId;
      const shouldBroadcastResolutions =
        prevStep &&
        prevStep !== step &&
        adminName &&
        adminName !== 'School User';

      if (shouldBroadcastResolutions) {
        if (prevStep === 'peer_review') {
          await broadcastAdminResolutionNotice({
            excludeUid: actor || null,
            label: `${adminName} cleared peer review for ${schoolLabel} (${prevStep} → ${step})`,
            predicate: (s) => !!s.permissions?.canPeerReview
          });
        }
        if (prevStep === 'internal_review_rejected') {
          await broadcastAdminResolutionNotice({
            excludeUid: actor || null,
            label: `${adminName} cleared peer-review revisions for ${schoolLabel} (${prevStep} → ${step})`,
            predicate: (s) => !!s.permissions?.canDraftReport
          });
        }
        if (prevStep === 'appeals') {
          await broadcastAdminResolutionNotice({
            excludeUid: actor || null,
            label: `${adminName} cleared fact-check / appeals for ${schoolLabel} (${prevStep} → ${step})`,
            predicate: (s) => !!s.permissions?.canPublish || !!s.permissions?.canManageStaff
          });
        }
      }
    } catch (err) {
      console.error("Workflow Update Error:", err);
      throw err;
    }
  },

  // 4b. Workflow Transitions
  submitForPeerReview: async (schoolId, schoolName, adminName) => {
    const reportRef = doc(db, "reports", schoolId);
    const schoolRef = doc(db, "schools", schoolId);
    await resetPeerReviewSignOff(reportRef);
    await updateDoc(reportRef, { 'meta.qaPhase': QA_PHASE_INITIAL });
    await updateDoc(schoolRef, { qaPhase: QA_PHASE_INITIAL });

    await adminServices.updateWorkflowStep(schoolId, 'peer_review', schoolName, adminName);

    const lifecycleRef = collection(db, "schools", schoolId, "lifecycle");
    await addDoc(lifecycleRef, {
      type: 'SUBMITTED_FOR_REVIEW',
      eventLabel: 'Report submitted for internal peer review',
      timestamp: serverTimestamp(),
      adminName: adminName,
    });
  },

  /** Accreditor sends post-appeal amended draft to QA (not to the school). */
  submitAppealForQa: async (schoolId, schoolName, adminName) => {
    const reportRef = doc(db, "reports", schoolId);
    const schoolRef = doc(db, "schools", schoolId);
    await resetPeerReviewSignOff(reportRef);
    await updateDoc(reportRef, { 'meta.qaPhase': QA_PHASE_POST_APPEAL });
    await updateDoc(schoolRef, { qaPhase: QA_PHASE_POST_APPEAL });

    await adminServices.updateWorkflowStep(schoolId, 'peer_review', schoolName, adminName);

    const lifecycleRef = collection(db, "schools", schoolId, "lifecycle");
    await addDoc(lifecycleRef, {
      type: 'APPEAL_SUBMITTED_FOR_QA',
      eventLabel: 'Amended draft submitted for post-appeal QA',
      timestamp: serverTimestamp(),
      adminName,
    });

    await adminServices.logAction(adminName, 'APPEAL_SUBMITTED_FOR_QA', { schoolId, schoolName });

    await adminServices.notifyRoles(
      ['SENIOR_ACCREDITOR', 'SUPER_ADMIN'],
      schoolId,
      {
        label: `Post-appeal QA required: ${schoolName}`,
        priority: 'red',
        category: 'reports',
        type: 'APPEAL_QA_REVIEW',
        isAction: true,
        actionPath: `/admin?tab=registry&schoolId=${schoolId}&mode=report`,
      }
    );
  },

  /** QA approves post-appeal draft for finalisation without another school fact-check. */
  approveAppealForFinalisation: async (schoolId, schoolName, adminName) => {
    const reportRef = doc(db, "reports", schoolId);
    const schoolRef = doc(db, "schools", schoolId);

    await updateDoc(reportRef, {
      'meta.qaPhase': deleteField(),
      'meta.lastEditedAt': serverTimestamp(),
    });

    await updateDoc(schoolRef, {
      workflowStep: 'accepted',
      qaPhase: deleteField(),
      lastWorkflowUpdate: serverTimestamp(),
    });

    const lifecycleRef = collection(db, "schools", schoolId, "lifecycle");
    await addDoc(lifecycleRef, {
      type: 'QA_APPROVED_FOR_FINALISATION',
      eventLabel: 'QA approved amended draft for finalisation (no further school fact-check)',
      timestamp: serverTimestamp(),
      adminName,
    });

    await adminServices.logAction(adminName, 'QA_APPROVE_APPEAL_FINAL', { schoolId, schoolName });

    const schoolSnap = await getDoc(schoolRef);
    const auditorUid = schoolSnap.data()?.assignedAuditorUid;
    if (auditorUid) {
      await adminServices.createNotification(auditorUid, schoolId, {
        label: `Ready for finalisation (QA approved): ${schoolName}`,
        priority: 'red',
        category: 'reports',
        type: 'ACCEPTED',
        isAction: true,
        actionPath: `/admin?tab=registry&schoolId=${schoolId}&mode=report`,
      });
    }

    await adminServices.notifyRoles(
      ['SENIOR_ACCREDITOR', 'SUPER_ADMIN'],
      schoolId,
      {
        label: `Ready for finalisation: ${schoolName}`,
        priority: 'red',
        category: 'reports',
        type: 'ACCEPTED',
        isAction: true,
        actionPath: `/admin?tab=registry&schoolId=${schoolId}&mode=report`,
      }
    );
  },

  preGenerateReportLens: async (schoolId, targetLang) => {
    const lang = (targetLang || 'en').toLowerCase();
    const reportRef = doc(db, 'reports', schoolId);
    const reportSnap = await getDoc(reportRef);
    if (!reportSnap.exists()) return { ok: false, reason: 'missing_report' };
    if (!lang || lang === 'en') {
      await updateDoc(reportRef, {
        'meta.translation.targetLang': 'en',
        'meta.translation.status': 'ready',
        'meta.translation.completedAt': serverTimestamp(),
        'meta.translation.errorMessage': deleteField()
      });
      return { ok: true, skipped: true, reason: 'english_only' };
    }

    const reportData = reportSnap.data();
    await updateDoc(reportRef, {
      'meta.translation.targetLang': lang,
      'meta.translation.status': 'processing',
      'meta.translation.errorMessage': deleteField()
    });

    const summaryDocId = `report_${schoolId}_summary`;
    const summarySource = { exec: reportData?.institutionalBrief?.executiveSummary || '' };
    await localizationService.sync(lang, summaryDocId, summarySource, 'v2_report_masters', 'v2_report_translations');

    const stationIds = Object.keys(reportData?.stationAnalysis || {});
    for (const sId of stationIds) {
      const source = { content: reportData?.stationAnalysis?.[sId]?.content || '' };
      const contentDocId = `report_${schoolId}_s${sId}`;
      await localizationService.sync(lang, contentDocId, source, 'v2_report_masters', 'v2_report_translations');
    }

    await updateDoc(reportRef, {
      'meta.translation.targetLang': lang,
      'meta.translation.status': 'ready',
      'meta.translation.completedAt': serverTimestamp(),
      'meta.translation.errorMessage': deleteField()
    });
    return { ok: true, stationCount: stationIds.length };
  },

  releaseToSchool: async (schoolId, schoolName, adminName) => {
    const schoolRef = doc(db, "schools", schoolId);
    const reportRef = doc(db, "reports", schoolId);
    const schoolSnap = await getDoc(schoolRef);
    const schoolData = schoolSnap.data();
    const recipient = adminServices.getSchoolEmail(schoolData); // Updated to use utility helper
    const schoolLang = getSchoolLangKey(schoolData);

    await adminServices.updateWorkflowStep(schoolId, 'fact_check', schoolName, adminName);

    await updateDoc(schoolRef, { qaPhase: deleteField() });
    await updateDoc(reportRef, {
      'meta.qaPhase': deleteField(),
      'meta.translation.targetLang': schoolLang,
      'meta.translation.status': schoolLang === 'en' ? 'ready' : 'queued',
      'meta.translation.queuedAt': serverTimestamp(),
      'meta.translation.errorMessage': deleteField(),
    });
    
    // 2. Create the Lifecycle Entry (School-specific record + Email trigger)
    const lifecycleRef = collection(db, "schools", schoolId, "lifecycle");
    await addDoc(lifecycleRef, {
      type: 'DRAFT_RELEASED',
      eventLabel: 'Accreditation Draft released to school for Fact-Check',
      timestamp: serverTimestamp(),
      adminName: adminName,
      recipient: recipient || "Email Not Found",
      emailTrigger: recipient ? {
        templateKey: 'email_factcheck',
        recipientEmail: recipient,
        language: schoolLang
      } : null
    });

    // 3. Log to Global Audit Trail (Visible in System Logs)
    await adminServices.logAction(adminName, "EMAIL_DISPATCHED", { 
      schoolId, 
      schoolName, 
      template: 'email_factcheck',
      recipient: recipient 
    });

    // Fire-and-forget pre-translation. Errors are reflected in report meta + logs.
    if (schoolData.assignedAuditorUid) {
      const ctx = schoolEmailContext(schoolData, schoolId);
      await adminServices.createNotification(schoolData.assignedAuditorUid, schoolId, {
        label: `Report released to school: ${ctx.schoolName}`,
        priority: 'red',
        category: 'reports',
        type: 'REPORT_RELEASED_TO_SCHOOL',
        isAction: true,
        actionPath: `/admin?tab=registry&schoolId=${schoolId}`,
        forceEmail: true,
        emailTemplateKey: 'email_staff_report_released',
        emailTemplateData: {
          ...ctx,
          reviewerName: adminName || 'IFIP Staff',
        },
      });
    }

    adminServices.preGenerateReportLens(schoolId, schoolLang).catch(async (err) => {
      console.error('preGenerateReportLens failed:', err);
      try {
        await updateDoc(reportRef, {
          'meta.translation.status': 'error',
          'meta.translation.errorMessage': err?.message || String(err),
          'meta.translation.completedAt': serverTimestamp()
        });
      } catch (writeErr) {
        console.error('Failed to persist translation error status:', writeErr);
      }
    });
  },

  submitReviewFeedback: async (schoolId, feedback, schoolName, reviewerName) => {
    const reportRef = doc(db, "reports", schoolId);
    await updateDoc(reportRef, {
      internalReviewFeedback: feedback,
      lastReviewAt: serverTimestamp(),
      reviewerName: reviewerName
    });
    await adminServices.updateWorkflowStep(schoolId, 'internal_review_rejected', schoolName);
    
    // Record in Lifecycle
    const lifecycleRef = collection(db, "schools", schoolId, "lifecycle");
    await addDoc(lifecycleRef, {
      type: 'REVIEW_REJECTED',
      eventLabel: 'Report returned to author for revisions by peer reviewer',
      timestamp: serverTimestamp(),
      adminName: reviewerName
    });

    const schoolSnap = await getDoc(doc(db, 'schools', schoolId));
    const schoolData = schoolSnap.exists() ? schoolSnap.data() : {};
    const ctx = schoolEmailContext(schoolData, schoolId);
    const reportSnap = await getDoc(reportRef);
    const leadUid = schoolData.assignedAuditorUid || reportSnap.data()?.meta?.authorUid;
    if (leadUid) {
      await adminServices.createNotification(leadUid, schoolId, {
        label: `Changes requested on report: ${ctx.schoolName}`,
        priority: 'red',
        category: 'reports',
        type: 'REVISION_REQUIRED',
        isAction: true,
        actionPath: `/admin?tab=registry&schoolId=${schoolId}&mode=report`,
        forceEmail: true,
        emailTemplateKey: 'email_staff_changes_requested',
        emailTemplateData: {
          ...ctx,
          reviewerName: reviewerName || 'IFIP Staff',
        },
      });
    }
    await adminServices.logAction(reviewerName, "PEER_REVIEW_REJECTED", { schoolId, schoolName });
  },

  retractReport: async (schoolId, targetStep, schoolName, adminName) => {
    // SECURITY LOCK: Prevent retraction of archived/finalised cycles // Added
    const schoolRef = doc(db, "schools", schoolId); // Added
    const snap = await getDoc(schoolRef); // Added
    if (snap.exists() && snap.data().workflowStep === 'finalised') { // Added
      throw new Error("Security Lock: Finalised reports cannot be retracted to drafting."); // Added
    } // Added

    await adminServices.updateWorkflowStep(schoolId, targetStep, schoolName, adminName);

    // If pulled back to drafting, invalidate previous peer sign-off.
    if (targetStep === 'report_compiling') {
      const reportRef = doc(db, "reports", schoolId);
      await updateDoc(reportRef, {
        "certification.peerReviewSigned": false,
        "certification.peerReviewerUid": null,
        "certification.peerReviewerName": null,
        "meta.lastEditedAt": serverTimestamp()
      });
    }

    // Record in Lifecycle
    const lifecycleRef = collection(db, "schools", schoolId, "lifecycle");
    await addDoc(lifecycleRef, {
      type: 'REPORT_RETRACTED',
      eventLabel: `Report retracted to ${targetStep.replace(/_/g, ' ')}`,
      timestamp: serverTimestamp(),
      adminName: adminName
    });

    await adminServices.logAction(adminName, "RETRACT_REPORT", { schoolId, schoolName, targetStep });
  },
  
  // 5. Save administrative actions to Firestore Audit Trail
  logAction: async (adminName, actionType, details) => {
    try {
      const logsRef = collection(db, "systemLogs");
      // Firestore rejects `undefined` anywhere in maps/arrays; PRICING diffs often set before/after to undefined.
      const safeDetails = JSON.parse(
        JSON.stringify(details == null ? {} : details, (_k, v) => (v === undefined ? null : v))
      );
      await addDoc(logsRef, {
        adminName: String(adminName || "Unknown"),
        actionType: String(actionType || "UNKNOWN"),
        details: safeDetails,
        timestamp: serverTimestamp(),
      });
      return true;
    } catch (err) {
      console.error("Logging Error:", actionType, err);
      return false;
    }
  },

  // 6. Refinery Lifecycle Management: Updates the "Traffic Light" progress
  updateRefineryStep: async (schoolId, type, step, label) => {
    const reportRef = doc(db, "reports", schoolId);
    const path = `meta.refinery.${type}`;
    
    await updateDoc(reportRef, {
      [path]: { step, label, updatedAt: serverTimestamp() }
    });

    await adminServices.logAction("System/AI", `REFINERY_${type.toUpperCase()}_STEP_${step}`, { schoolId });
  },

  // 7. Total Refinery Reset (Protocol for Recalling a Submission)
  recallSubmission: async (schoolId, schoolName) => {
    const auditRef = doc(db, "audits", schoolId);
    const reportRef = doc(db, "reports", schoolId);
    const schoolRef = doc(db, "schools", schoolId);
    const distilledRef = doc(db, "distilledData", schoolId);
    const intelligenceRef = doc(db, "globalIntelligence", schoolId); // Silo B

    // 1. Move Audit back to Draft
    await updateDoc(auditRef, { 
      status: 'Draft',
      recalledAt: serverTimestamp()
    });

    // 2. Reset the Refinery LEDs
    await updateDoc(reportRef, {
      "meta.refinery": {
        evidence: { step: 0, label: 'recalled' },
        survey: { step: 0, label: 'recalled' }
      }
    });

    // 3. Delete BOTH Distillation Silos (Destroy the "Ghost Data")
    try {
      await deleteDoc(distilledRef);
      await deleteDoc(intelligenceRef);
    } catch (e) {
      console.warn("One or more silos not found, skipping delete.");
    }

    // 4. Sync Institutional Workflow
    await updateDoc(schoolRef, {
      workflowStep: 'recalled_to_draft',
      lastWorkflowUpdate: serverTimestamp()
    });

    await adminServices.logAction("Super Admin", "RECALL_SUBMISSION", { schoolId, schoolName });
  },

  returnSefToSchoolForEditing: async ({
    schoolId,
    schoolName,
    adminName,
    reason,
    suggestionMessageEn = "",
    suggestionMessageLocalized = "",
    localizedLangKey = "en"
  }) => {
    const reasonKey = reason === 'accreditor_suggestion' ? 'accreditor_suggestion' : 'school_request';
    await adminServices.recallSubmission(schoolId, schoolName);

    const schoolRef = doc(db, "schools", schoolId);
    const schoolSnap = await getDoc(schoolRef);
    if (!schoolSnap.exists()) return;
    const schoolData = schoolSnap.data();
    const recipient = adminServices.getSchoolEmail(schoolData);
    if (!recipient) throw new Error("No institutional email on file.");

    const language = getSchoolLangKey(schoolData);
    const shouldUseLocalizedMessage = language !== 'en' && suggestionMessageLocalized && resolveLangKey(localizedLangKey) === language;
    const messageForEmail = shouldUseLocalizedMessage ? suggestionMessageLocalized : suggestionMessageEn;

    const lifecycleRef = collection(db, "schools", schoolId, "lifecycle");
    await addDoc(lifecycleRef, {
      type: "SEF_RETURNED_TO_SCHOOL",
      eventLabel: reasonKey === 'school_request'
        ? "SEF returned to school at school's request"
        : "SEF returned to school with accreditor suggestion",
      timestamp: serverTimestamp(),
      adminName: adminName || 'IFIP Staff',
      reason: reasonKey,
      emailTrigger: {
        templateKey: reasonKey === 'school_request'
          ? 'email_sef_return_school_request'
          : 'email_sef_return_accreditor_suggestion',
        recipientEmail: recipient,
        language,
        templateData: {
          orgLead: schoolData.answers?.orgLead || "Lead Contact",
          reasonLabel: reasonKey === 'school_request' ? "School's Request" : "Accreditor's Suggestion",
          ifipMessage: messageForEmail || ""
        }
      }
    });

    await adminServices.logAction(adminName || "IFIP Staff", "SEF_RETURN_TO_SCHOOL", {
      schoolId,
      schoolName,
      reason: reasonKey,
      language,
      hadLocalizedMessage: Boolean(suggestionMessageLocalized)
    });
  },

  // 8. Forensic Data Entry: Saves Questionnaire to Dual-Silos
// Patch:
  saveQuestionnaire: async (schoolId, data) => { // Removed genAI parameter // Changed
    const rawRef = doc(db, "questionnaires_raw", schoolId);
    const intelligenceRef = doc(db, "globalIntelligence", schoolId);
    const reportRef = doc(db, "reports", schoolId);
    const schoolRef = doc(db, "schools", schoolId); // Reference to update the lock status // Added

    // Silo 1: Raw Record (Full context for audit trail)
    await setDoc(rawRef, { 
      responses: data, 
      submittedAt: serverTimestamp() 
    });

    // Silo 2: Intelligence Shell (Prepares the AI extraction path)
    await setDoc(intelligenceRef, { 
      source: 'questionnaire',
      status: 'pending_extraction',
      extractedAt: null 
    }, { merge: true });

    // Update the school record to trigger the "Locked" state in UI // Added
    await updateDoc(schoolRef, { // Added
      lastForensicUpdate: serverTimestamp() // Added
    }); // Added

    // TRIGGER AUTOMATED REFINERY FOR SILO B
    adminServices.runSurveyRefinery(schoolId); // Removed genAI parameter // 

    await adminServices.logAction("School/User", "SUBMIT_FORENSIC_INDEX", { schoolId });
  },

  // 9. The Refinery Brain: Multi-Lens AI Extraction
// Patch:
  runRefineryExtraction: async (schoolId) => { // Removed genAI parameter // Changed
    const pipelineMode = await getAiPipelineMode();
    // Additive: v2 first, then v1; legacy body below unchanged.
    if (isSchemaV2Pipeline(pipelineMode)) {
      return runRefineryExtractionSchemaV2(schoolId, {
        actor: { uid: auth.currentUser?.uid, email: auth.currentUser?.email },
      });
    }
    if (isSchemaV1Pipeline(pipelineMode)) {
      return runRefineryExtractionSchemaV1(schoolId, {
        updateRefineryStep: adminServices.updateRefineryStep,
      });
    }
    const reportRef = doc(db, "reports", schoolId);
    const distilledRef = doc(db, "distilledData", schoolId); // Only focusing on Silo A // Changed

    try {
      // 1. Advance light to Step 1 (Extracting)
      await adminServices.updateRefineryStep(schoolId, 'evidence', 1, 'extracting');

      // 2. Distill raw files into Silo A
      const distilledData = await adminServices.distillEvidence(schoolId); // Removed genAI parameter // Changed
      
      // 3. Advance light to Step 3 (Synthesising)
      await adminServices.updateRefineryStep(schoolId, 'evidence', 3, 'synthesising');

      // 4. Analysis Prompt (Restored anonymizedSummary for context)
      const prompt = `
        You are the IFIP Evidence Analysis Engine. Analyze this DISTILLED evidence.
        INSTITUTIONAL OVERVIEW: ${distilledData.anonymizedSummary}
        DISTILLED FACT SHEET: ${JSON.stringify(distilledData.factSheet)}

        TASK: For EACH of the 7 IFIP Stations, provide a station-specific verification lens based ONLY on the evidence provided.
        
        - 2 "Evidence Anchors": Specific proof points found in the distilled facts.
        - 1 "Evidence Gap": A narrative claim usually associated with this station that is NOT supported by these specific files.
        - CRITICAL: "anchors" and "gaps" MUST be arrays of short plain strings only. Do NOT return objects, factSheet rows, or fileName/category/proves structures.

        OUTPUT: Return ONLY a raw JSON object:
        { "stations": { "01": { "anchors": ["string", "string"], "gaps": ["string"] }, ... (up to 07) } }
      `;

      const extraction = await geminiGenerateJson(prompt);

      // 5. Map the AI verification anchors to each station in the Report
      const updates = {};
      const stations = normalizeExtractionStations(extraction.stations || {});
      Object.keys(stations).forEach(sId => {
        updates[`stationAnalysis.${sId}.verification`] = stations[sId];
      });
      updates["meta.refinery.evidence"] = { step: 4, label: 'stored' };
      
      await updateDoc(reportRef, updates);
      await adminServices.logAction("AI/System", "SILO_A_EXTRACTION_COMPLETE", { schoolId });

      try {
        const schoolSnap = await getDoc(doc(db, "schools", schoolId));
        const schoolLabel = schoolSnap.exists() ? (schoolSnap.data()?.officialName || schoolId) : schoolId;
        const actor = actorUid();
        const actorName = auth.currentUser?.displayName || "IFIP Staff";
        await broadcastAdminResolutionNotice({
          excludeUid: actor,
          label: `${actorName} ran evidence refinery (Silo A) for ${schoolLabel}`,
          predicate: (s) => !!s.permissions?.canRunAI || s.role === 'SUPER_ADMIN' || s.role === 'SENIOR_ACCREDITOR'
        });
      } catch (_e) {}

    } catch (err) {
      console.error("Silo A Extraction Failed:", err);
      await updateDoc(reportRef, { "meta.refinery.evidence": { step: 1, label: 'extraction_error' } });
    }
  },

  // 9b. Automated Silo B Extraction (Forensic Index) // Added
  runSurveyRefinery: async (schoolId) => { // Removed genAI parameter // Changed
    const reportRef = doc(db, "reports", schoolId); // Added
    const questRef = doc(db, "questionnaires_raw", schoolId); // Added
    const intelligenceRef = doc(db, "globalIntelligence", schoolId); // Added

    try { // Added
      // Step 1: Advance light to Indexing // Added
      await adminServices.updateRefineryStep(schoolId, 'survey', 1, 'indexing'); // Added
      
      const questSnap = await getDoc(questRef); // Added
      const questData = questSnap.data()?.responses || {}; // Added

      // Step 3: Advance light to Synthesising // Added
      await adminServices.updateRefineryStep(schoolId, 'survey', 3, 'synthesising'); // Added

      const prompt = `Extract forensic metrics from this data: ${JSON.stringify(questData)}. Return ONLY raw JSON: { "spend": "", "staff": "", "retention": "" }`;
      
      const metrics = await geminiGenerateJson(prompt);

      // Save to the Intelligence Silo (B) // Added
      await setDoc(intelligenceRef, { // Added
        metrics, // Added
        status: 'harvested', // Added
        lastUpdated: serverTimestamp() // Added
      }, { merge: true }); // Added

      // Step 4: Advance light to Stored // Added
      await adminServices.updateRefineryStep(schoolId, 'survey', 4, 'stored'); // Added
    } catch (err) { // Added
      console.error("Silo B Extraction Failed:", err); // Added
      await updateDoc(reportRef, { "meta.refinery.survey": { step: 1, label: 'error' } }); // Added
    } // Added
  }, // Added

  // 10. The Report Generation Engine: Formal Synthesis of all Lenses
  generateReportDraft: async (schoolId, user, targetLang = 'English') => { // Removed genAI parameter // Changed
    const pipelineMode = await getAiPipelineMode();
    if (isSchemaV2Pipeline(pipelineMode)) {
      return generateReportDraftSchemaV2({
        schoolId,
        user,
        targetLang,
        deps: {
          migrateReportStaffWorkspaceFromLegacyIfNeeded:
            adminServices.migrateReportStaffWorkspaceFromLegacyIfNeeded,
          logAction: adminServices.logAction,
          reportStaffWorkspaceRef,
        },
      });
    }
    if (isSchemaV1Pipeline(pipelineMode)) {
      return generateReportDraftSchemaV1({
        schoolId,
        user,
        targetLang,
        deps: {
          migrateReportStaffWorkspaceFromLegacyIfNeeded:
            adminServices.migrateReportStaffWorkspaceFromLegacyIfNeeded,
          logAction: adminServices.logAction,
          reportStaffWorkspaceRef,
        },
      });
    }
    const schoolRef = doc(db, "schools", schoolId);
    const auditRef = doc(db, "audits", schoolId);
    const reportRef = doc(db, "reports", schoolId);
    const wsRef = reportStaffWorkspaceRef(schoolId);
// Patch:
    const distilledRef = doc(db, "distilledData", schoolId); // Only focusing on Silo A // Changed

    try {
    await adminServices.migrateReportStaffWorkspaceFromLegacyIfNeeded(schoolId);
    // Fetch Institutional DNA, SEF, Distilled Evidence Silo, and staff-only workspace (precis + contact log)
    const [schoolSnap, auditSnap, reportSnap, distilledSnap, wsSnap] = await Promise.all([
      getDoc(schoolRef),
      getDoc(auditRef),
      getDoc(reportRef),
      getDoc(distilledRef),
      getDoc(wsRef),
    ]);

    const schoolRaw = schoolSnap.data();
    const schoolData = schoolRaw?.answers || {};
    const auditData = auditSnap.data() || { stations: {}, evidenceVault: [] };
    const fullReportData = reportSnap.data() || {};
    const wsData = wsSnap.exists() ? wsSnap.data() : {};
    const contactLogForPrompt = Array.isArray(wsData.accreditorContactLog) && wsData.accreditorContactLog.length
      ? wsData.accreditorContactLog
      : (fullReportData?.meta?.accreditorContactLog || []);
    const internalPrecisForPrompt =
      typeof wsData.internalNotes === 'string' && wsData.internalNotes.trim() !== ''
        ? wsData.internalNotes
        : (typeof fullReportData.internalNotes === 'string' ? fullReportData.internalNotes : '');
    
    // Distilled Data from Silo A only
    const distilledData = distilledSnap.exists() ? distilledSnap.data() : { factSheet: [], anonymizedSummary: "" };

    if (!fullReportData.meta?.authorUid) {
      await updateDoc(reportRef, { "meta.authorUid": user.uid });
    }

      const prompt = `
        SYSTEM: You are the IFIP Lead Accreditation Auditor. Generate a definitive, 9-page formal report for ${schoolRaw?.officialName}.
        
        MANDATORY REQUIREMENT: The entire report must be written in ${targetLang}. 
        If ${targetLang} is a language code (e.g., 'es', 'ar'), use the corresponding natural language (e.g., Spanish, Arabic).

        INPUT DATA:
        1. INSTITUTIONAL DNA: ${JSON.stringify(schoolData)}
        2. SELF-EVALUATION (SEF): ${JSON.stringify(auditData.stations)}
        3. EVIDENCE REPOSITORY: ${auditData.evidenceVault?.map(f => f.name).join(", ")}
        4. Silo A (DISTILLED EVIDENCE): ${JSON.stringify(distilledData)}
        5. ACCREDITOR CONTACT / EXCHANGE LOG (staff-only; chronological notes from calls or emails with the institution; internal context onlyâ€”never quote verbatim in school-facing narrative): ${JSON.stringify(contactLogForPrompt)}
        6. INTERNAL ACCREDITOR PRECIS (staff-only running notes; do not surface verbatim in the school-facing report body): ${JSON.stringify(internalPrecisForPrompt || '')}
        7. DISCIPLINARY EXCLUSIONS (registration): schoolExclusions in INSTITUTIONAL DNA records instance counts (not unique pupils) for temporary suspensions and permanent expulsions across current and previous academic years. Weave into behavioural climate, safeguarding, inclusion culture, and relevant station narratives where material.

        STRICT AUDIT GUIDELINES:
        - INDEPENDENT JUDGMENT: Analyze the SEF against Silo A and Silo B. If forensic metrics or anchors contradict the self-selected level, you MUST override and propose a clinical, justified level (1-5).
        - THE GOLDEN THREAD: Maintain narrative consistency across all 7 stations. For example, if Silo A shows low inclusion spend, reflect this in Station 1 (Resources).
        - EVIDENCE PORTFOLIO (Section 04): Build "evidencePortfolio.registry" as a clinical mirror of the evidence vault using Silo A: one object per file in the EVIDENCE REPOSITORY list (match fileName to Silo A factSheet entries where possible). Each entry: fileName, category, evidenceSummary, proves, strength (1-5). Also set "evidencePortfolio.gaps" to an aggregated list of missing or weak evidence from Silo A.
        - STRATEGIC ROADMAP (Section 05): "strategicRoadmap.content" MUST begin with one or two concluding paragraphs that synthesise the report (tone: formal closure, not a bullet memo). After the paragraph(s), add numbered recommendations ONLY as separate lines, each starting with a digit, a full stop, and a space (e.g. "1. ..." then newline "2. ..."). Include at least three numbered recommendations where appropriate.
        - NEXT REVIEW ("strategicRoadmap.nextPhase"): State whether the institution appears ready to progress to the next IFIP accreditation tier within a realistic window; if not yet ready, recommend continued work toward that tier with IFIP support (name concrete IFIP offerings where relevant). This is advisory language for the school, not a contractual guarantee.
        - ROADMAP SELECTION: Among numbered recommendations, include at least two items drawn from the IFIP Service Menu: [Follow-up Desktop Audit (6m), Stage Progression (12m), IFIP Foundations of Inclusion Course for all staff, Specialist Masterclasses, Bespoke Training].
        - TONE: Professional, authoritative, and clinical. Avoid marketing language. Do not use em dashes or bold text.
        - SAFETY LOGIC: If Silo A is missing, rely on the narrative but flag "Verification Deficit" in the internalNotes field of your JSON output (this field is persisted staff-only, not shown to the school) and report in the evidence gaps.
        - PUBLIC PORTRAIT (institutionalBrief.executiveSummary):
          Write a 250-350 word pen portrait of the school for public sharing—not a conventional executive summary.
          Purpose: a concise, warmly affirmative overview the school will be proud to display on its website and on the IFIP accreditation badge page.
          Tone: professional, human, and broadly positive; celebrate distinctive culture, inclusion practice, and evidenced strengths drawn from the audit.
          Coverage: where the evidence supports it, weave in themes that reflect the full breadth of the school's inclusion journey across all seven IFIP framework areas (e.g. leadership and culture, resources, teaching and learning, assessment, wellbeing, partnerships, and outcomes)—but as integrated narrative, not a checklist. Do not name, number, or refer to "stations", "sections", or the IFIP framework structure explicitly.
          Omissions: omit or lightly downplay development points, gaps, and recommendations (those belong in station analysis, evidence gaps, and the strategic roadmap). If a particular area is a source of concern or significant development need and you cannot honestly describe it in positive, shareable terms, do not mention that area at all—silence is preferable to faint praise or implied criticism.
          Integrity: this is not a whitewash—do not invent achievements; every claim must be supportable from the inputs. Where evidence is thin, use careful, general language rather than criticism.
          Style: readable prose (not bullet points); no marketing slogans; no em dashes or bold text; do not include maturity scores, station ratings, or tables in this field.

        OUTPUT: Return ONLY a raw JSON object:
        {
          "institutionalBrief": { "executiveSummary": "250-350 word public pen portrait of the school (shareable, broadly positive; development points omitted or downplayed)" },
          "stationAnalysis": {
            "01": { "ifipRating": 1-5, "content": "Formal evaluation text...", "verification": { "anchors": ["plain string proof point"], "gaps": ["plain string gap"] } },
            "02": { "ifipRating": 1-5, "content": "Formal evaluation text...", "verification": { "anchors": ["plain string proof point"], "gaps": ["plain string gap"] } },
            "03": { "ifipRating": 1-5, "content": "Formal evaluation text...", "verification": { "anchors": ["plain string proof point"], "gaps": ["plain string gap"] } },
            "04": { "ifipRating": 1-5, "content": "Formal evaluation text...", "verification": { "anchors": ["plain string proof point"], "gaps": ["plain string gap"] } },
            "05": { "ifipRating": 1-5, "content": "Formal evaluation text...", "verification": { "anchors": ["plain string proof point"], "gaps": ["plain string gap"] } },
            "06": { "ifipRating": 1-5, "content": "Formal evaluation text...", "verification": { "anchors": ["plain string proof point"], "gaps": ["plain string gap"] } },
            "07": { "ifipRating": 1-5, "content": "Formal evaluation text...", "verification": { "anchors": ["plain string proof point"], "gaps": ["plain string gap"] } }
          },
          "evidencePortfolio": {
            "registry": [ { "fileName": "example.pdf", "category": "", "evidenceSummary": "", "proves": "", "strength": 3 } ],
            "gaps": "Aggregated list of missing or weak evidence..."
          },
          "strategicRoadmap": { "content": "Opening synthesis paragraph(s).\\n\\n1. First numbered recommendation\\n2. Second...\\n3. Third...", "nextPhase": "Readiness for next tier vs continued progression with IFIP support, with indicative timing", "resources": "IFIP links" },
          "internalNotes": "Auditor precis flagging contradictions or concerns"
        }

        CRITICAL: stationAnalysis.*.verification.anchors and .gaps MUST be arrays of short plain strings only. Never put factSheet/registry objects (fileName/category/evidenceSummary/proves) inside anchors or gaps — those objects belong only in evidencePortfolio.registry.
      `;

      const reportContent = await geminiGenerateJson(prompt);

      if (!reportContent.evidencePortfolio || typeof reportContent.evidencePortfolio !== 'object') {
        reportContent.evidencePortfolio = { registry: [], gaps: '' };
      }
      if (!Array.isArray(reportContent.evidencePortfolio.registry)) {
        reportContent.evidencePortfolio.registry = [];
      }
      normalizeStationAnalysisVerifications(reportContent.stationAnalysis);

      // Institutional Maturity Score = mean of station ifipRatings (also refreshed on manual edits)
      const avgScore = computeOverallMaturityScore(reportContent.stationAnalysis);

      const aiInternalPrecis = reportContent.internalNotes;
      delete reportContent.internalNotes;

      // Update the school-readable report document (internal precis lives only on staff_workspace)
      await updateDoc(reportRef, {
        ...reportContent,
        "meta.isGenerated": true,
        "meta.generatedAt": serverTimestamp(),
        "meta.overallMaturityScore": avgScore,
        workflowStep: 'ai_draft_ready'
      });

      if (aiInternalPrecis !== undefined && aiInternalPrecis !== null) {
        await setDoc(
          wsRef,
          { internalNotes: String(aiInternalPrecis), updatedAt: serverTimestamp() },
          { merge: true }
        );
      }

      await adminServices.logAction("AI/System", "REPORT_GENERATION_COMPLETE", { schoolId });

    } catch (err) {
      console.error("Report Generation Failed:", err);
      throw err;
    }
  },

  // 11. The Evidence Distiller: Creates the "Clean Silo" from Raw Evidence
  distillEvidence: async (schoolId) => { // Removed genAI parameter // Changed
    const pipelineMode = await getAiPipelineMode();
    if (isSchemaV2Pipeline(pipelineMode)) {
      // V2 uses per-file jobs via runRefineryExtractionSchemaV2; seed schema only if distill called alone.
      const { seedSchoolSchemaFromRegistration } = await import('./aiPipelines/schemaV2');
      return seedSchoolSchemaFromRegistration(schoolId);
    }
    if (isSchemaV1Pipeline(pipelineMode)) {
      return distillEvidenceSchemaV1(schoolId);
    }
    const auditRef = doc(db, "audits", schoolId);
    const distilledRef = doc(db, "distilledData", schoolId);

    try {
      const auditSnap = await getDoc(auditRef);
      const { evidenceVault = [], stations = {} } = auditSnap.data() || {};

      const prompt = `
        You are the IFIP Data Forensic Officer. Transform this raw Evidence Vault into a "Clinical Fact Sheet."
        
        INPUT:
        - Filenames: ${evidenceVault.map(f => f.name).join(", ")}
        - Station Narratives: ${JSON.stringify(stations)}

        TASK:
        1. ANONYMIZE: Remove all specific names of students or staff. Replace with roles (e.g., "Student A", "Lead Teacher").
        2. CATEGORIZE: Group files into: Financial, Policy, Pedagogical, or Physical/Sensory.
        3. EXTRACT INTENT: For EVERY file listed, provide a 1-sentence clinical description of what it is and what it proves.
        4. STRENGTH RATING: Assign a 1-5 score for "Evidence Density".

        OUTPUT: Return ONLY a raw JSON object:
        {
          "factSheet": [
            { "fileName": "exact_original_filename.pdf", "category": "", "evidenceSummary": "", "proves": "", "strength": 1-5 }
          ],
          "anonymizedSummary": "A 200-word overview of the institutional capacity demonstrated by this vault.",
          "globalMetrics": { "totalFiles": 0, "primaryCategory": "" }
        }
      `;

      const distillation = await geminiGenerateJson(prompt);

      // Save to the Clean Silo (distilledData collection)
      await setDoc(distilledRef, {
        ...distillation,
        distilledAt: serverTimestamp(),
        sourceAuditVersion: auditSnap.data()?.lastUpdated || null
      });

      return distillation;
    } catch (err) {
      console.error("Distillation Error:", err);
      throw err;
    }
  },
  // 12. Turn-Based Traffic Light Logic (The Auditor's Triage Engine)
  getAuditTurn: resolveAuditTurn,

  /**
   * Registry "Action Required" buckets — same decision order as {@link getAuditTurn} for `school` only
   * (`audit` / `report` omitted). Used to group rows; only meaningful when `getAuditTurn(...).priority === 1`.
   */
  getRegistryActionBucket: resolveRegistryActionBucket,

  // 13. Stealth Benchmarking: Captures a snapshot for global dataset
  /** Optional `reportSnapshot` avoids reading `reports/{sid}` after live doc reset post-finalise. */
  publishToBenchmarks: async (schoolId, reportSnapshot = null) => {
    try {
      const reportRef = doc(db, "reports", schoolId);
      const schoolRef = doc(db, "schools", schoolId);
      const benchmarkRef = doc(db, "globalBenchmarks", schoolId);

      const schoolSnap = await getDoc(schoolRef);
      if (!schoolSnap.exists()) return;

      let reportData = reportSnapshot;
      if (!reportData) {
        const reportSnap = await getDoc(reportRef);
        if (!reportSnap.exists()) return;
        reportData = reportSnap.data();
      }

      const schoolData = schoolSnap.data();

      // Recompute from stations so benchmarks match any accreditor rating tweaks
      const overallMaturityScore = computeOverallMaturityScore(reportData.stationAnalysis);

      await setDoc(benchmarkRef, {
        schoolId,
        officialName: schoolData.officialName,
        country: schoolData.answers?.officialAddressCountry || "Unknown",
        region: schoolData.answers?.region || "Global",
        accreditationLevel: normalizeAccreditationLevel(schoolData.accreditationLevel),
        overallMaturityScore,
        publishedAt: serverTimestamp(),
        stationRatings: Object.keys(reportData.stationAnalysis || {}).reduce((acc, sId) => {
          acc[sId] = reportData.stationAnalysis[sId]?.ifipRating;
          return acc;
        }, {})
      });
    } catch (err) {
      console.error("Benchmarking Error:", err);
    }
  },

  // 14. Finalize Cycle: Snapshots the report and triggers the FINAL email
  finalizeAccreditation: async (schoolId, user) => {
    const reportRef = doc(db, "reports", schoolId);
    const schoolRef = doc(db, "schools", schoolId);

    await adminServices.migrateReportStaffWorkspaceFromLegacyIfNeeded(schoolId);

    const [reportSnap, schoolSnap] = await Promise.all([
      getDoc(reportRef), getDoc(schoolRef)
    ]);

    if (!reportSnap.exists()) throw new Error("Report not found");
    const reportDataRaw = reportSnap.data();
    const schoolData = schoolSnap.data();
    const publicBadgeId = schoolData?.publicBadgeId || generatePublicBadgeId();
    const reportData = await mergeCertificationFromLeadStaff(reportDataRaw, schoolData);
    const reportForSchoolFacing = stripStaffOnlyFromReportSnapshot(reportData);
    // Ensure archived overall score matches station ratings at finalise time
    const overallMaturityScore = computeOverallMaturityScore(reportForSchoolFacing.stationAnalysis);
    const reportSnapshotForIssue = {
      ...reportForSchoolFacing,
      meta: {
        ...(reportForSchoolFacing.meta || {}),
        overallMaturityScore,
      },
    };
    const recipient = adminServices.getSchoolEmail(schoolData); // Updated to use utility helper

    const timestamp = new Date();
    const awardedLevel = normalizeAccreditationLevel(schoolData.accreditationLevel);
    const archiveId = `${awardedLevel}_${timestamp.getMonth()+1}_${timestamp.getFullYear()}`;
    const archiveRef = doc(db, "schools", schoolId, "reports_archive", archiveId);

    // Immutable archive (source of record for school-facing final report)
    const publishedAt = reportSnapshotForIssue.meta?.publishedAt || serverTimestamp();
    await setDoc(archiveRef, {
      ...reportSnapshotForIssue,
      accreditationLevel: awardedLevel,
      meta: {
        ...(reportSnapshotForIssue.meta || {}),
        isPublished: true,
        publishedAt,
        finalizedAt: serverTimestamp(),
        publicBadgeId,
        accreditationLevel: awardedLevel,
      },
      archivedAt: serverTimestamp(),
      archivedBy: user.displayName,
      snapshotLabel: `${awardedLevel.toUpperCase()} - ${timestamp.toLocaleString('default', { month: 'long' })} ${timestamp.getFullYear()}`
    });

    // Benchmark snapshot uses in-memory report before live doc is reset
    await adminServices.publishToBenchmarks(schoolId, reportSnapshotForIssue);

    // Track global sample size for school-portal benchmarking readiness (see `getBenchmarkReadiness`)
    try {
      await setDoc(doc(db, "settings", "benchmarking"), {
        publishedReportsCount: increment(1)
      }, { merge: true });
    } catch (e) {
      console.warn("Benchmarking settings counter update skipped:", e);
    }

    // Update School Status - Portal remains active but workflow is locked
    await updateDoc(schoolRef, {
      workflowStep: 'finalised',
      // status: 'inactive', // Point 7: Keep portal open for report access
      publicBadgeId,
      finalReportIssuedAt: serverTimestamp(), // Point 3: Explicit date for Hub UI
      lastWorkflowUpdate: serverTimestamp()
    });

    // Verify pages fall back to schools.publicBadgeId if this map is missing.
    // A denied write must not abort the completion email or live-report reset.
    try {
      await setDoc(doc(db, "publicBadges", publicBadgeId), {
        schoolId,
        publicBadgeId,
        level: awardedLevel,
        issuedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }, { merge: true });
    } catch (badgeErr) {
      console.warn("publicBadges map write skipped:", badgeErr);
      await adminServices.logAction(user.displayName || user.email || "IFIP Staff", "PUBLIC_BADGE_MAP_FAILED", {
        schoolId,
        schoolName: schoolData.officialName,
        publicBadgeId,
        summary: badgeErr?.message || "publicBadges write failed.",
      });
    }

    // Point 7: Lock the SEF narratives to Read-Only
    await updateDoc(doc(db, "audits", schoolId), { status: 'Finalised' });

    // RECORD IN LIFECYCLE + TRIGGER FINAL EMAIL
    const lifecycleRef = collection(db, "schools", schoolId, "lifecycle");
    await addDoc(lifecycleRef, {
      type: 'CYCLE_COMPLETED',
      eventLabel: 'Final Report & Digital Badge Issued',
      timestamp: serverTimestamp(),
      adminName: user.displayName,
      recipient: recipient || "Email Not Found",
      emailTrigger: recipient ? {
        templateKey: 'email_final',
        recipientEmail: recipient,
        language: getSchoolLangKey(schoolData),
        templateData: {
          orgLead: schoolData.answers?.orgLead || "Lead Contact"
        }
      } : null
    });

    // Log to Global Audit Trail
    await adminServices.logAction(user.displayName, "FINALISE_ACCREDITATION", { 
      schoolId, 
      schoolName: schoolData.officialName, 
      recipient 
    });

    if (schoolData.assignedAuditorUid) {
      await adminServices.createNotification(schoolData.assignedAuditorUid, schoolId, {
        label: `Report Finalized: ${schoolData.officialName}`,
        priority: 'amber',
        category: 'reports',
        type: 'FINALISED',
        isAction: false
      });
    }

    // Next cycle must not inherit prior staff precis / contact log (subcollection survives root `setDoc`)
    await setDoc(
      reportStaffWorkspaceRef(schoolId),
      {
        internalNotes: '',
        accreditorContactLog: [],
        clearedAfterFinaliseAt: serverTimestamp(),
      },
      { merge: true }
    );

    // Reset live report for the next accreditation cycle (archive remains canonical for the completed cycle)
    await setDoc(reportRef, buildInitialReportPayload());

    try {
      await adminServices.publishAccreditedSchoolToPublicShowcase(
        schoolId,
        user.displayName || user.email || 'IFIP Staff',
        { allowExpired: false }
      );
    } catch (showcaseErr) {
      console.warn('Accredited showcase auto-publish skipped:', showcaseErr);
      await adminServices.logAction(user.displayName || user.email || 'IFIP Staff', 'ACCREDITED_SHOWCASE_SYNC_FAILED', {
        schoolId,
        schoolName: schoolData.officialName,
        summary: showcaseErr?.message || 'Accredited showcase auto-publish failed.',
      });
    }
  },

  /** School portal: read `settings/benchmarking` (public read rule) for placeholder vs live benchmarking UI. */
  getBenchmarkReadiness: async () => {
    try {
      const snap = await getDoc(doc(db, "settings", "benchmarking"));
      const d = snap.exists() ? snap.data() : {};
      const minSampleSize = typeof d.minSampleSize === "number" && d.minSampleSize >= 0 ? d.minSampleSize : 100;
      const sampleCount = typeof d.publishedReportsCount === "number" && d.publishedReportsCount >= 0 ? d.publishedReportsCount : 0;
      return { ready: sampleCount >= minSampleSize, minSampleSize, sampleCount };
    } catch (e) {
      console.warn("getBenchmarkReadiness:", e);
      return { ready: false, minSampleSize: 100, sampleCount: 0 };
    }
  },
    
    // 15. Retrieve archive list for the school portal
    getReportArchive: async (schoolId) => {
      const { getDocs, collection, orderBy, query } = await import('firebase/firestore');
      const archiveRef = collection(db, "schools", schoolId, "reports_archive");
      const q = query(archiveRef, orderBy("archivedAt", "desc"));
      const snap = await getDocs(q);
      return snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    },

    // 16. School Acceptance Logic
    acceptReportDraft: async (schoolId, schoolName) => {
      const schoolRef = doc(db, "schools", schoolId);
      await updateDoc(schoolRef, {
        workflowStep: 'accepted',
        lastWorkflowUpdate: serverTimestamp()
      });

      // Lifecycle + systemLogs for acceptance are written by Cloud Function
      // `onSchoolHandoffComplianceLog` (Admin SDK) so magic-link schools need not hit staff-only collections.

      // Staff emails for school accept: Cloud Function onSchoolHandoffComplianceLog (Admin SDK).
    },

    // 17. School Feedback / Appeal Logic
    submitFactCheckFeedback: async (schoolId, feedback, schoolName) => {
      const reportRef = doc(db, "reports", schoolId);
      const schoolRef = doc(db, "schools", schoolId);
      const schoolSnap = await getDoc(schoolRef);
      const preferredLang = getSchoolLangKey(schoolSnap.data() || {});
      const normalizedFeedback = FACT_CHECK_SECTION_KEYS.reduce((acc, key) => {
        const raw = feedback?.[key];
        acc[key] = typeof raw === 'string' ? raw : '';
        return acc;
      }, {});
      const translation = await backTranslateFactCheckFeedback(normalizedFeedback);
      const sourceLang = translation.language && translation.language !== 'unknown'
        ? translation.language
        : preferredLang;
      const needsTranslation = sourceLang !== 'en';

      const structured = {
        version: 1,
        submittedAt: serverTimestamp(),
        language: sourceLang,
        status: needsTranslation ? 'translated' : 'not_needed',
        general: {
          originalText: normalizedFeedback.general || '',
          originalLang: sourceLang,
          translatedEn: (translation.translated?.general || normalizedFeedback.general || ''),
          translationStatus: needsTranslation ? 'translated' : 'not_needed'
        },
        sections: {
          s01: {
            originalText: normalizedFeedback.s01 || '',
            originalLang: sourceLang,
            translatedEn: (translation.translated?.s01 || normalizedFeedback.s01 || ''),
            translationStatus: needsTranslation ? 'translated' : 'not_needed'
          },
          s02: {
            originalText: normalizedFeedback.s02 || '',
            originalLang: sourceLang,
            translatedEn: (translation.translated?.s02 || normalizedFeedback.s02 || ''),
            translationStatus: needsTranslation ? 'translated' : 'not_needed'
          },
          s03: {
            originalText: normalizedFeedback.s03 || '',
            originalLang: sourceLang,
            translatedEn: (translation.translated?.s03 || normalizedFeedback.s03 || ''),
            translationStatus: needsTranslation ? 'translated' : 'not_needed'
          },
          s04: {
            originalText: normalizedFeedback.s04 || '',
            originalLang: sourceLang,
            translatedEn: (translation.translated?.s04 || normalizedFeedback.s04 || ''),
            translationStatus: needsTranslation ? 'translated' : 'not_needed'
          },
          s05: {
            originalText: normalizedFeedback.s05 || '',
            originalLang: sourceLang,
            translatedEn: (translation.translated?.s05 || normalizedFeedback.s05 || ''),
            translationStatus: needsTranslation ? 'translated' : 'not_needed'
          },
          s06: {
            originalText: normalizedFeedback.s06 || '',
            originalLang: sourceLang,
            translatedEn: (translation.translated?.s06 || normalizedFeedback.s06 || ''),
            translationStatus: needsTranslation ? 'translated' : 'not_needed'
          },
          s07: {
            originalText: normalizedFeedback.s07 || '',
            originalLang: sourceLang,
            translatedEn: (translation.translated?.s07 || normalizedFeedback.s07 || ''),
            translationStatus: needsTranslation ? 'translated' : 'not_needed'
          }
        }
      };
      
      await resetPeerReviewSignOff(reportRef);
      await updateDoc(reportRef, {
        factCheckFeedback: feedback, // Legacy shape retained for compatibility
        feedbackSubmittedAt: serverTimestamp(),
        factCheck: structured,
        'meta.qaPhase': deleteField(),
      });

      await updateDoc(schoolRef, {
        workflowStep: 'appeals',
        qaPhase: deleteField(),
        lastWorkflowUpdate: serverTimestamp(),
      });

    // School ack + staff emails: Cloud Function onSchoolHandoffComplianceLog (Admin SDK).
    // Client cannot write notifications/lifecycle from magic-link school sessions.

        // systemLogs for this handoff: Cloud Function `onSchoolHandoffComplianceLog`
    },

    /** Remove onboarding approval alerts for a staff member (PERM_canManageStaff queue). */
    dismissStaffApprovalNotifications: async (staffUid) => {
      const { query, where, getDocs, collection } = await import('firebase/firestore');
      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef,
        where('uid', '==', 'PERM_canManageStaff'),
        where('linkId', '==', staffUid)
      );
      const snap = await getDocs(q);
      const toDelete = snap.docs.filter((d) => (d.data() || {}).type === 'STAFF_APPROVAL');
      if (!toDelete.length) return;
      await Promise.all(toDelete.map((d) => deleteDoc(d.ref)));
    },

    // 18. Persistent Notifications (with Email Trigger Support)
    createNotification: async (uid, linkId, data) => {
      const { query, where, getDocs, collection } = await import('firebase/firestore');
      const notificationsRef = collection(db, "notifications");
      
      const q = query(notificationsRef, where("uid", "==", uid), where("linkId", "==", linkId));
      const existing = await getDocs(q);
      await Promise.all(existing.docs.map(d => deleteDoc(d.ref)));

      await addDoc(notificationsRef, {
        uid,
        linkId,
        ...data,
        isDismissible: true,
        isAction: data.isAction !== false,
        actionPath: data.isAction === false ? null : (data.actionPath || null),
        suppressEmail: data.suppressEmail === true,
        // Backend trigger (`onNotificationCreate`) should only email true actionable items.
        emailTemplateKey: data.emailTemplateKey || null,
        emailTemplateData: data.emailTemplateData || {},
        emailTrigger:
          data.suppressEmail === true || data.isAction === false
            ? 'NONE'
            : (data.forceEmail === true || data.priority === 'red')
              ? 'IMMEDIATE'
              : 'DIGEST',
        fromIdentity: 'accreditation@ifip.group',
        inboundReply: 'info@ifip.group',
        createdAt: serverTimestamp()
      });
    },

    dismissNotification: async (notificationId) => {
      await deleteDoc(doc(db, "notifications", notificationId));
    }
    ,

    /**
     * Bulk-dismiss FYI notifications for the signed-in staff member.
     * Does not touch dynamic Action Centre tasks (those are derived from live registry state).
     */
    dismissMyInformationalNotifications: async (uidsToScan = []) => {
      const { query, where, getDocs, limit } = await import('firebase/firestore');
      const targets = Array.from(new Set((uidsToScan || []).filter(Boolean)));
      if (!targets.length) return { deleted: 0 };

      const shouldDeleteDoc = (d) => {
        const x = d.data() || {};
        if (x.type === 'DIAGNOSTIC' || x.linkId === 'DIAGNOSTIC_TASK') return false;
        if (x.isAction === false) return true;
        if (x.type === 'RESOLUTION') return true;
        return false;
      };

      let deleted = 0;
      const seen = new Set();

      const deleteQueryPages = async (q) => {
        // Repeatedly pull a page and delete eligible docs until the page contains none.
        for (let pass = 0; pass < 50; pass += 1) {
          const snap = await getDocs(q);
          if (snap.empty) return;

          let batch = writeBatch(db);
          let ops = 0;
          let progressed = false;

          for (const d of snap.docs) {
            if (seen.has(d.id)) continue;
            if (!shouldDeleteDoc(d)) continue;
            seen.add(d.id);
            batch.delete(d.ref);
            ops += 1;
            deleted += 1;
            progressed = true;
            if (ops >= 450) {
              await batch.commit();
              batch = writeBatch(db);
              ops = 0;
            }
          }

          if (ops > 0) await batch.commit();
          if (!progressed) return;
        }
      };

      for (const uid of targets) {
        await deleteQueryPages(query(collection(db, "notifications"), where("uid", "==", uid), where("isAction", "==", false), limit(200)));
        await deleteQueryPages(query(collection(db, "notifications"), where("uid", "==", uid), where("type", "==", "RESOLUTION"), limit(200)));
      }

      return { deleted };
    },

    triggerDiagnosticEmail: async (templateKey, language, targetEmail, adminName, adminUid) => { // Replacement: Added adminUid parameter
    // Use a fresh doc id for each run so Firestore triggers are deterministic.
    const normalizedTemplate =
      templateKey === 'magic_link' ? 'email_new_registration' :
      templateKey === 'resumption' ? 'email_resume_registration' :
      templateKey;
    const testId = `IFIP_DIAGNOSTIC_${Date.now()}`;
    const testRef = doc(db, "schools", testId);
    const lifecycleRef = collection(db, "schools", testId, "lifecycle");

    const baseSchool = {
      officialName: "IFIP Diagnostic Lab",
      officialEmail: targetEmail,
      signupLanguage: language,
      status: 'inactive',
      paymentStatus: 'awaiting_invoice',
      magicLinkRequested: false,
      answers: {
        officialEmail: targetEmail,
        q3: targetEmail,
        orgLead: "Test Recipient",
        q1: "Test Recipient"
      }
    };

    if (normalizedTemplate === 'email_activation_pending') {
      // onSchoolWrite expects a fresh create with awaiting_invoice.
      await setDoc(testRef, {
        ...baseSchool,
        lastAdminAction: serverTimestamp()
      });
    } 
    else if (normalizedTemplate === 'email_new_registration') {
      // onSchoolWrite expects transition inactive -> active.
      await setDoc(testRef, {
        ...baseSchool,
        paymentStatus: 'paid',
        status: 'inactive',
        magicLinkRequested: false
      });
      await updateDoc(testRef, { 
        status: 'active',
        magicLinkRequested: false,
        lastAdminAction: serverTimestamp() 
      });
    }
    else if (normalizedTemplate === 'email_resume_registration') {
      // onSchoolWrite expects magicLinkRequested false -> true while inactive.
      await setDoc(testRef, {
        ...baseSchool,
        status: 'inactive',
        magicLinkRequested: false
      });
      await updateDoc(testRef, { 
        status: 'inactive', 
        magicLinkRequested: true,
        lastAdminAction: serverTimestamp() 
      });
    }
    else if (normalizedTemplate === 'email_factcheck' || normalizedTemplate === 'email_final') {
      // Triggers onLifecycleWrite logic for workflow gates.
      await setDoc(testRef, {
        ...baseSchool,
        paymentStatus: 'paid'
      }, { merge: true });
      await addDoc(lifecycleRef, {
        type: 'DIAGNOSTIC_TEST',
        eventLabel: `Manual Diagnostic: ${normalizedTemplate}`,
        timestamp: serverTimestamp(),
        adminName: adminName,
        emailTrigger: {
          templateKey: normalizedTemplate,
          recipientEmail: targetEmail,
          language: language,
          templateData: { orgLead: "Test Recipient" }
        }
      });
    }
    else if (templateKey.startsWith('admin_')) {
      throw new Error("Admin diagnostic templates are retired. Use live workflow notifications instead.");
    }

    await adminServices.logAction(adminName, "DIAGNOSTIC_EMAIL_SENT", { templateKey, targetEmail });
  }
    ,
  // --- STAGE 2: PRICING & FEE MANAGEMENT ---
  getPricingConfig: async () => {
    const pricingRef = doc(db, "settings", "pricing");
    const snap = await getDoc(pricingRef);
    if (snap.exists()) {
      const data = snap.data() || {};
      const registrationsPaused =
        data.registrationsPaused === true || data.globalMarketsDisabled === true;
      const normalized = {
        ...data,
        schemaVersion: Number(data.schemaVersion) >= 2 ? 2 : 1,
        baseRates: data.baseRates || { foundation: 1000, certified: 2000, accredited: 3000 },
        tierOverrides: cloneJsonSafeMaps(data.tierOverrides),
        countryOverrides: cloneJsonSafeMaps(data.countryOverrides),
        disabledMarkets: cloneJsonSafeMaps(data.disabledMarkets),
        countryRules: cloneJsonSafeMaps(data.countryRules),
        registrationsPaused,
        globalMarketsDisabled: registrationsPaused,
      };
      normalized.countryRules = hydrateCountryRulesForAdmin(normalized);
      return normalized;
    }
    const fresh = {
      schemaVersion: 2,
      baseRates: { foundation: 1000, certified: 2000, accredited: 3000 },
      tierOverrides: {},
      countryOverrides: {},
      disabledMarkets: {},
      countryRules: {},
      registrationsPaused: false,
      globalMarketsDisabled: false,
    };
    fresh.countryRules = hydrateCountryRulesForAdmin(fresh);
    return fresh;
  },

  savePricingConfig: async (config, adminName) => {
    const pricingRef = doc(db, "settings", "pricing");
    const publicRef = doc(db, "public", "fees"); // Feed for external website
    const econRef = doc(db, "settings", "economics"); // Backend mapping source

    const prevSnap = await getDoc(pricingRef);
    const previousDoc = prevSnap.exists() ? prevSnap.data() : null;
    const payload = buildSanitizedPricingDocForFirestore(config, adminName);
    const audit = buildPricingAuditDetails(previousDoc, payload);

    const batch = writeBatch(db);
    batch.set(pricingRef, payload);
    batch.set(publicRef, payload);
    await batch.commit();
    await setDoc(econRef, { worldBankTiers: WORLD_BANK_TIERS });

    const changesForLog = Array.isArray(audit.changes)
      ? audit.changes.slice(0, MAX_PRICING_CHANGES_IN_LOG)
      : [];
    const omitted =
      typeof audit.changeCount === "number" && audit.changeCount > changesForLog.length
        ? audit.changeCount - changesForLog.length
        : 0;

    const auditLogged = await adminServices.logAction(adminName || "IFIP Staff", "PRICING_UPDATE", {
      summary: audit.summary,
      changeCount: audit.changeCount,
      changes: changesForLog,
      changesOmitted: omitted > 0 ? omitted : 0,
    });
    return { auditLogged: auditLogged === true };
  },

  getCalculatedFee: (country, level, config) => {
    if (!config) {
      return { amount: 0, available: true, mode: 'online', requiresPartnerCode: false, requiresStripe: true };
    }
    return resolveCalculatedFee(country, level, config);
  },

  /** Server fee quote: GBP canonical + `displayLine` for local currency + Stripe charge resolution. */
  getAccreditationFeeQuote: async (country, level, offerCode = '') => {
    const getFee = httpsCallable(getFunctions(), 'getRegistrationFee');
    const { data } = await getFee({ country, level, offerCode: offerCode || '' });
    return data;
  },

  getOffersConfig: async () => {
    const ref = doc(db, 'settings', 'offers');
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const data = snap.data() || {};
      return { codes: data.codes || {} };
    }
    return { codes: {} };
  },

  saveOffersConfig: async (config, adminName) => {
    const ref = doc(db, 'settings', 'offers');
    const prevSnap = await getDoc(ref);
    const prevCodes = prevSnap.exists() ? (prevSnap.data().codes || {}) : {};
    const nextCodes = config?.codes || {};
    const payload = {
      codes: nextCodes,
      updatedAt: serverTimestamp(),
      updatedBy: adminName || 'IFIP Staff',
    };
    await setDoc(ref, payload, { merge: true });
    const prevKeys = Object.keys(prevCodes);
    const nextKeys = Object.keys(nextCodes);
    await adminServices.logAction(adminName || 'IFIP Staff', 'OFFERS_UPDATE', {
      summary: `Offers updated (${nextKeys.length} active codes).`,
      previousCount: prevKeys.length,
      nextCount: nextKeys.length,
    });
    return { ok: true };
  },

  validateRegistrationOfferCode: async (code, level = '', country = '', schoolId = '') => {
    const fn = httpsCallable(getFunctions(), 'validateRegistrationOffer');
    const { data } = await fn({
      code,
      level: level || undefined,
      country: country || undefined,
      schoolId: schoolId || undefined,
    });
    return data;
  },

  confirmExternalRegistrationPayment: async (schoolId, adminName, details = {}) => {
    const fn = httpsCallable(getFunctions(), 'confirmExternalRegistrationPayment');
    const payload = { schoolId };
    if (details.amountGbp != null && details.amountGbp !== '') {
      payload.amountGbp = Number(details.amountGbp);
    }
    if (details.paidAt) payload.paidAt = details.paidAt;
    if (details.externalReference) payload.externalReference = details.externalReference;
    if (details.note) payload.note = details.note;
    const { data } = await fn(payload);
    await adminServices.logAction(adminName, 'EXTERNAL_PAYMENT_CONFIRMED', {
      schoolId,
      amountGbp: payload.amountGbp ?? null,
      externalReference: payload.externalReference || null,
      summary: 'External partner payment marked as received.',
    });
    return data;
  },

  startRenewalWithPartnerOffer: async ({
    schoolId,
    country,
    level,
    offerCode,
    cycleRef,
    requestType,
    actorName,
    submitDetails,
  }) => {
    const fn = httpsCallable(getFunctions(), 'startRenewalWithPartnerOffer');
    const origin = typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'https://accreditation.ifip.group';
    const { data } = await fn({
      schoolId,
      country,
      level,
      offerCode,
      cycleRef,
      requestType,
      actorName,
      submitDetails,
      portalOrigin: origin,
    });
    return data;
  },

  markSchoolSefSubmitted: async (schoolId) => {
    const fn = httpsCallable(getFunctions(), 'markSchoolSefSubmitted');
    const { data } = await fn({ schoolId });
    return data;
  },

  activateRegistrationWithPartnerOffer: async ({ schoolId, country, level, offerCode }) => {
    const fn = httpsCallable(getFunctions(), 'activateRegistrationWithPartnerOffer');
    const origin = typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'https://accreditation.ifip.group';
    const { data } = await fn({
      schoolId,
      country,
      level,
      offerCode,
      portalOrigin: origin,
    });
    return data;
  },

  // --- Flagship Schools (ifip.group accreditation website carousel) ---
  getShowcaseDraft: async () => {
    const ref = doc(db, 'settings', 'showcase');
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      return { entries: [], lastPublishedAt: null };
    }
    const data = snap.data() || {};
    const entries = Array.isArray(data.entries) ? data.entries : [];
    return {
      entries: entries.map((e, i) => ({
        id: String(e.id || ''),
        name: String(e.name || ''),
        country: String(e.country || ''),
        city: String(e.city || ''),
        level: 'flagship',
        logoUrl: e.logoUrl || null,
        storagePath: e.storagePath || null,
        sortOrder: Number.isFinite(Number(e.sortOrder)) ? Number(e.sortOrder) : i,
      })),
      lastPublishedAt: data.lastPublishedAt || null,
      updatedBy: data.updatedBy || null,
    };
  },

  saveShowcaseDraft: async (entries, adminName) => {
    const ref = doc(db, 'settings', 'showcase');
    const sanitized = (entries || []).map((e, i) => ({
      id: String(e.id || '').trim(),
      name: String(e.name || '').trim(),
      country: String(e.country || '').trim(),
      city: String(e.city || '').trim(),
      level: 'flagship',
      logoUrl: e.logoUrl ? String(e.logoUrl) : null,
      storagePath: e.storagePath ? String(e.storagePath) : null,
      sortOrder: Number.isFinite(Number(e.sortOrder)) ? Number(e.sortOrder) : i,
    }));
    await setDoc(
      ref,
      {
        entries: sanitized,
        updatedAt: serverTimestamp(),
        updatedBy: String((adminName || '').trim() || 'IFIP Staff'),
      },
      { merge: true }
    );
    return { entryCount: sanitized.length };
  },

  readPublicSchoolShowcase: async () => {
    const publicRef = doc(db, 'public', PUBLIC_SHOWCASE_DOC_ID);
    const snap = await getDoc(publicRef);
    if (!snap.exists()) {
      return { flagshipSchools: [], accreditedSchools: [], schools: [] };
    }
    const data = snap.data() || {};
    const { flagshipSchools, accreditedSchools } = splitShowcaseRows(data);
    return {
      ...data,
      flagshipSchools,
      accreditedSchools,
      schools: mergeShowcaseSchools(flagshipSchools, accreditedSchools),
    };
  },

  publishAccreditedSchoolToPublicShowcase: async (schoolId, adminName, options = {}) => {
    const { allowExpired = false } = options;
    const schoolRef = doc(db, 'schools', schoolId);
    const schoolSnap = await getDoc(schoolRef);
    if (!schoolSnap.exists()) throw new Error('School not found');

    const school = schoolSnap.data();
    const archive = await adminServices.getReportArchive(schoolId);
    const latestArchive = archive[0] || null;
    const eligibility = evaluateAccreditedShowcaseEligibility(school, latestArchive, {
      allowExpired,
    });
    if (!eligibility.ok) {
      throw new Error(eligibility.reason || 'School cannot be published to the corporate site.');
    }

    const entry = buildAccreditedShowcaseEntry(schoolId, school, latestArchive);
    if (!entry.name) throw new Error('School name is required before publishing.');

    const firestoreEntry = toFirestoreAccreditedRow(entry);
    const existing = await adminServices.readPublicSchoolShowcase();
    const accreditedSchools = upsertAccreditedRow(existing.accreditedSchools, firestoreEntry);
    const flagshipSchools = existing.flagshipSchools || [];
    const schools = mergeShowcaseSchools(flagshipSchools, accreditedSchools);
    const staffLabel = String((adminName || '').trim() || 'IFIP Staff');

    const publicRef = doc(db, 'public', PUBLIC_SHOWCASE_DOC_ID);
    await setDoc(
      publicRef,
      {
        schemaVersion: SHOWCASE_SCHEMA_VERSION,
        publishedAt: serverTimestamp(),
        updatedBy: staffLabel,
        flagshipSchools,
        accreditedSchools,
        schools,
      },
      { merge: true }
    );

    await updateDoc(schoolRef, {
      publicShowcaseAccreditedPublishedAt: serverTimestamp(),
    });

    await adminServices.logAction(staffLabel, 'ACCREDITED_SHOWCASE_PUBLISH', {
      summary: `Accredited school published to ifip.group: ${entry.name}.`,
      schoolId,
      schoolName: entry.name,
    });

    return { entry, accreditedCount: accreditedSchools.length };
  },

  rebuildAccreditedPublicShowcase: async (options = {}) => {
    const fn = httpsCallable(getFunctions(), 'rebuildAccreditedShowcase');
    const { data } = await fn({
      dryRun: !!options.dryRun,
      allowExpired: !!options.allowExpired,
    });
    return data || {};
  },

  getSchoolPurgeContext: async (schoolId) => {
    const fn = httpsCallable(getFunctions(), 'getSchoolPurgeContext');
    const { data } = await fn({ schoolId });
    return data || {};
  },

  purgeSchoolCompletely: async (payload) => {
    const fn = httpsCallable(getFunctions(), 'purgeSchoolCompletely');
    const { data } = await fn(payload);
    return data || {};
  },

  publishSchoolShowcase: async (adminName) => {
    const draft = await adminServices.getShowcaseDraft();
    const sorted = [...(draft.entries || [])].sort(
      (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
    );
    const flagshipSchools = sorted.map((e, i) => {
      const level = 'flagship';
      return {
        id: e.id,
        name: e.name,
        country: e.country,
        city: e.city || '',
        level,
        levelLabel: 'Flagship',
        logoUrl: e.logoUrl || null,
        sortOrder: Number.isFinite(Number(e.sortOrder)) ? Number(e.sortOrder) : i,
        source: 'curated',
      };
    });

    const existing = await adminServices.readPublicSchoolShowcase();
    const accreditedSchools = existing.accreditedSchools || [];
    const schools = mergeShowcaseSchools(flagshipSchools, accreditedSchools);
    const staffLabel = String((adminName || '').trim() || 'IFIP Staff');

    const publicRef = doc(db, 'public', PUBLIC_SHOWCASE_DOC_ID);
    const draftRef = doc(db, 'settings', 'showcase');
    const batch = writeBatch(db);
    batch.set(
      publicRef,
      {
        schemaVersion: SHOWCASE_SCHEMA_VERSION,
        publishedAt: serverTimestamp(),
        updatedBy: staffLabel,
        flagshipSchools,
        accreditedSchools,
        schools,
      },
      { merge: true }
    );
    batch.set(
      draftRef,
      { lastPublishedAt: serverTimestamp() },
      { merge: true }
    );
    await batch.commit();

    await adminServices.logAction(staffLabel, 'SHOWCASE_PUBLISH', {
      summary: `Flagship Schools published to ifip.group (${flagshipSchools.length} flagship, ${accreditedSchools.length} accredited).`,
      flagshipCount: flagshipSchools.length,
      accreditedCount: accreditedSchools.length,
    });
    return {
      schoolCount: schools.length,
      flagshipCount: flagshipSchools.length,
      accreditedCount: accreditedSchools.length,
    };
  },
};
